import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { DateClickArg } from '@fullcalendar/interaction';
import { EventClickArg, EventInput } from '@fullcalendar/core';
import deLocale from '@fullcalendar/core/locales/de';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Link,
  ListSubheader,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewOutlined from '@mui/icons-material/OpenInNewOutlined';
import { api } from '../../helpers/apiUtils';
import moment from 'moment-timezone';
import {
  Feld,
  GefahrBestaetigung,
  Kennzeichen,
  MehrOptionen,
  NurAdmin,
} from '../../components/marketingForm';
import { BEGRIFF_LIVE, BESTAETIGEN_BIS, KENNZEICHEN_LIVE, TECHNIK_KONTAKT } from './texteLive';
import { AngebotDialog, lageFehlt, type AngebotErgebnis, type AngebotLage } from './AngebotDialog';
import AdHeadline, {
  ladeSchlagzeile,
  NEWSLETTER_VERSAND,
  speichereSchlagzeile,
} from './AdHeadline';

/** Eine Zeile aus `live_show_override_changes`. */
interface OverrideChange {
  field: string;
  old_value: string | null;
  new_value: string | null;
  changed_by_email: string | null;
  changed_at: string;
}

/** Deutsche Feldnamen fuer den Verlauf — die Spalten heissen englisch. */
const OVERRIDE_FIELD_LABELS: Record<string, string> = {
  scheduled_time: 'Uhrzeit',
  action: 'Art',
  label: 'Titel',
  notes: 'Notiz',
  host_email: 'Moderator',
  broadcast_source: 'Sendungsart',
  recorded_video_id: 'Aufzeichnung',
};

interface LiveShow {
  date: string;
  time: string;
  iso: string;
  source: 'default' | 'reschedule' | 'extra' | 'test';
  label: string | null;
  is_today: boolean;
  is_test: boolean;
  host_email: string | null;
  // Confirmation-gate state (2026-07-24). 'test' shows always ='confirmed'
  // (exempt from the gate). 'extra' / 'reschedule' default to 'pending' on
  // creation → 'confirmed' after moderator confirms → 'cancelled' via 08:50
  // auto-cutoff or manual click. Consumer gates (newsletter live-notice,
  // T-15 reminder, /status für Zuschauer) require 'confirmed'.
  status?: 'pending' | 'confirmed' | 'cancelled';
  // 'manual' | 'auto_no_confirm' (Cutoff 08:50, LiveShowConfirmationService.runCutoffCron).
  cancel_reason?: string | null;
  // Export-Sperre: dieser Sendetag geht NICHT in den ai-prod-Poll und damit
  // weder auf YouTube noch in den automatischen FT-Artikel. Das öffentliche
  // Replay-Archiv bleibt unberührt.
  ai_export_blocked?: boolean;
  ai_export_block_reason?: string | null;
  // Sendungsart aus dem Kalender (P3/D5): eine Aufzeichnung heisst im Kalender auch so.
  broadcast_source?: 'live' | 'recorded';
  // Anzahl gespeicherter Themen des Tages; fehlt, wenn der Server sie nicht lesen konnte.
  topic_count?: number;
}

// Fehlermeldung einer Termin-Route: Klartext aus `details`/`message`, sonst der Code (P3/C2, C3).
function fehlerText(err: unknown, fallback: string): string {
  const e = err as {
    response?: { data?: { error?: string; details?: unknown; message?: string } };
    message?: string;
  };
  const data = e?.response?.data;
  if (typeof data?.details === 'string' && data.details) return data.details;
  if (typeof data?.message === 'string' && data.message) return data.message;
  return data?.error || e?.message || fallback;
}

const AKTION_TEXT: Record<string, string> = {
  extra: 'Sendung',
  test: 'Test',
  default: 'Regulärer Termin (alter Sendeplan)',
  reschedule: 'Verschobener Termin (alter Sendeplan)',
};

interface HolidayInfo {
  date: string;
  name: string;
}

interface AvailableHost {
  email: string;
  /** Anzeigename (Server: `LiveScheduleService.resolveModeratorName`). Fehlt bei altem Server. */
  name?: string | null;
}

// Manual-Only-Modus: nur noch echte Sendung ('extra') oder Test ('test') aus dem Dialog.
// Legacy-Werte ('default' / 'cancel' / 'reschedule') kommen weiterhin aus der API für bereits
// existierende Rows — werden gerendert, aber nicht mehr neu geschrieben.
type Action = 'extra' | 'test';

type LiveKennzeichen = keyof typeof KENNZEICHEN_LIVE;
/** Kennzeichen-Props für `Feld` mit dem Live-Tooltip (der Standard spricht von Webinaren). */
const kz = (art: LiveKennzeichen) => ({ art, kennzeichenErklaerung: KENNZEICHEN_LIVE[art] });

/**
 * Kalender-Kategorien (Persona-Runde 0, C1): die Farbe sagt den STATUS. Bis dahin war die Farbe
 * die Termin-Art — eine unbestätigte Sendung sah aus wie eine bestätigte.
 */
type Kategorie = 'bestaetigt' | 'aufzeichnung' | 'offen' | 'abgesagt' | 'test';
const KATEGORIE: Record<Kategorie, { label: string; bg: string; border: string; text: string }> = {
  bestaetigt: { label: 'Bestätigt', bg: '#c8e6c9', border: '#2e7d32', text: '#1b5e20' },
  aufzeichnung: {
    label: 'Aufzeichnung (bestätigt)',
    bg: '#d1c4e9',
    border: '#5e35b1',
    text: '#311b92',
  },
  offen: { label: 'Noch nicht bestätigt', bg: '#ffe0b2', border: '#ef6c00', text: '#8a3c00' },
  abgesagt: { label: 'Abgesagt', bg: '#fde0dc', border: '#c62828', text: '#b71c1c' },
  // Blau statt Rosa (Persona-Runde 2, Sandra: „Abgesagt“ und „Test“ waren kaum zu unterscheiden).
  test: { label: 'Test (nur intern)', bg: '#bbdefb', border: '#1565c0', text: '#0d47a1' },
};

const HOLIDAY_COLOR = { bg: '#eceff1', border: '#90a4ae', text: '#37474f' };

function kategorieVon(s: LiveShow): Kategorie {
  if (s.source === 'test') return 'test';
  if (s.status === 'cancelled') return 'abgesagt';
  if (s.status === 'pending') return 'offen';
  return s.broadcast_source === 'recorded' ? 'aufzeichnung' : 'bestaetigt';
}

const WOCHENTAGE = [
  'Sonntag',
  'Montag',
  'Dienstag',
  'Mittwoch',
  'Donnerstag',
  'Freitag',
  'Samstag',
];

/** `2026-10-29` → „Donnerstag, 29.10.“ (Jahr nur, wenn es nicht das laufende ist). Lokale Teile, kein UTC. */
function datumLang(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const jahr = Number(m[1]) === new Date().getFullYear() ? '' : m[1];
  return `${WOCHENTAGE[d.getDay()]}, ${m[3]}.${m[2]}.${jahr}`;
}

/** Der Termin, der beim Öffnen an diesem Tag gespeichert war (für die Ersetzen-Warnung). */
interface Bestand {
  source: LiveShow['source'];
  time: string;
}

/**
 * Folgen eines bestätigten Termins ohne Themen (Persona-Runde 1, Felix: „Pflicht“ ist unglaubwürdig, solange
 * es bestätigte Termine mit 0 Themen gibt). Belegt:
 *   - live.finanztrends.de zeigt unter dem Countdown statt der Themen „Bleib dran · Live-Sendung gleich“
 *     (`node-app/public/live/index.html`, `renderCockpitTopics`),
 *   - ohne eigenen Titel heißt die Aufzeichnung im Archiv nur „Kursgespräche“ (`routes/live.ts`,
 *     `deriveReplayTitle`).
 * Der Newsletter (Prod-Template „minimalistic“) zeigt keine Themen — dort ändert sich nichts.
 */
const THEMEN_FEHLEN_FOLGE =
  'Zuschauer sehen auf live.finanztrends.de vor der Sendung statt der Themen nur „Bleib dran · Live-Sendung gleich“, und ohne eigenen Titel heißt die Aufzeichnung im Archiv nur „Kursgespräche“.';

/**
 * Wie ein Test startet (Persona-Runde 1, Sandra). Belegt:
 *   - `LiveAutoGoLiveService` behandelt Tests wie echte Sendungen: sendet das Studio, geht der Termin von
 *     der Uhrzeit bis 5 Minuten danach automatisch live (`TRIGGER_WINDOW_MIN = 5`, `waehleFaelligenTermin`),
 *   - ein Start von Hand („Live gehen“) wird bis 90 Minuten Abstand dem Termin zugerechnet
 *     (`liveYoutube/terminBindung.ts`, `TOLERANZ_MIN = 90`) und bleibt damit ein Test,
 *   - sichtbar nur mit Test-Link (`routes/live.ts`, `gateTestBroadcast`).
 */
/**
 * Vorgabe-Uhrzeit für einen neuen Termin: 10:00 — außer heute nach 09:45, dann die nächste volle
 * Viertelstunde mindestens 15 Minuten voraus (ein Test „heute Nachmittag" stand sonst auf 10:00, also
 * in der Vergangenheit).
 */
function standardUhrzeit(datum: string): string {
  // Berliner Zeit, nicht die des Browsers: gespeichert wird die Uhrzeit als Berliner Wallclock.
  const jetzt = moment.tz('Europe/Berlin');
  if (datum !== jetzt.format('YYYY-MM-DD') || jetzt.format('HH:mm') < '09:45') return '10:00';
  const ziel = jetzt.clone().add(15, 'minutes');
  const rest = ziel.minute() % 15;
  if (rest > 0 || ziel.second() > 0) ziel.add(15 - rest, 'minutes');
  return ziel.isSame(jetzt, 'day') ? ziel.format('HH:mm') : '23:45';
}

const TEST_START_SATZ =
  'So startet der Test: im Studio vorher die Übertragung starten. Kommt zur eingestellten Uhrzeit ein Signal aus dem Studio, geht der Test von selbst live (bis 5 Minuten danach). Später oder ohne Signal aus dem Studio: im Reiter „Heute“ auf „Live gehen“ klicken — bis 90 Minuten nach der Uhrzeit zählt das noch als dieser Test.';

/**
 * Uhrzeit der Bestätigungs-Mail an die Moderation (Cron `live_show_confirmation_reminder`,
 * `LiveShowConfirmationService.runReminderCron`). Sie geht NUR an Termine mit `status = 'pending'`
 * (ebd., SELECT … WHERE status = 'pending') — eine abgesagte Sendung bekommt keine. Ging sie schon raus,
 * setzt ihr Link „Bestätigen“ eine inzwischen abgesagte Sendung WIEDER an: `confirm()` behandelt
 * 'cancelled' als Wieder-Ansetzen, und die Mail-Seite bietet das Bestätigen bei 'cancelled' weiter an
 * (`routes/live.ts`, GET /schedule/confirm/:token).
 */
const MODERATIONSMAIL_UM = '08:30';

/**
 * Was der Test-Link zeigt. Belegt: der Link ist für alle Tests derselbe (`?ftpreview=<FT_LIVE_TEST_TOKEN>`,
 * `routes/live.ts` /admin/status `test_vorschau_url`); er hebt nur das Test-Gate auf (`gateTestBroadcast`),
 * zeigt also, was `/status` für den NÄCHSTEN gespeicherten Termin liefert — ab 5 Minuten vor Beginn den
 * Vorlauf (`PRE_SHOW_WINDOW_SEC`), danach die Übertragung. Ein ungespeicherter Test existiert dort nicht.
 */
const TEST_LINK_SATZ =
  'Der Link ist für alle Tests derselbe und zeigt nur einen gespeicherten Test: ab 5 Minuten vor der Uhrzeit den Vorlauf mit Countdown, danach die Übertragung. Vor dem Speichern ist dort nichts vom Test zu sehen. Liegt vorher noch eine echte Sendung, zeigt er zuerst diese.';

interface ScheduleProps {
  /** Berliner `YYYY-MM-DD`: Termin-Dialog dieses Tages öffnen (Sprung aus „Heute“). */
  oeffneDatum?: string | null;
  /** Wird gerufen, sobald `oeffneDatum` verarbeitet ist (Dialog offen oder Fehler gemeldet). */
  onOeffneDatumErledigt?: () => void;
}

const Schedule: FC<ScheduleProps> = ({ oeffneDatum, onOeffneDatumErledigt }) => {
  const [shows, setShows] = useState<LiveShow[]>([]);
  const [holidays, setHolidays] = useState<HolidayInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // Visible range driven by FullCalendar's datesSet callback
  const visibleRangeRef = useRef<{ from: string; to: string } | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDate, setDialogDate] = useState<string>('');
  const [dialogAction, setDialogAction] = useState<Action>('extra');
  const [dialogTime, setDialogTime] = useState<string>('10:00');
  const [dialogLabel, setDialogLabel] = useState<string>('');
  const [dialogNotes, setDialogNotes] = useState<string>('');
  const [dialogBusy, setDialogBusy] = useState(false);
  const [dialogDeleting, setDialogDeleting] = useState(false);
  // Gespeicherter Stand des Tages (Art + Status). Wird nach einem erfolgreichen Speichern im
  // offenen Dialog nachgezogen, damit Kopf-Status und Knöpfe den neuen Stand zeigen.
  const [dialogExistingSource, setDialogExistingSource] = useState<LiveShow['source'] | null>(null);
  const [dialogExistingStatus, setDialogExistingStatus] = useState<LiveShow['status'] | null>(null);
  const [dialogCancelReason, setDialogCancelReason] = useState<string | null>(null);
  const [dialogBestand, setDialogBestand] = useState<Bestand | null>(null);
  const [dialogConfirming, setDialogConfirming] = useState(false);
  const [dialogCancelling, setDialogCancelling] = useState(false);
  // Rückmeldung IM Dialog (Persona-Runde 0, A2): „gespeichert, aber nicht bestätigt" darf nicht in
  // einem Toast verschwinden, der nach 3,5 s weg ist.
  const [dialogMeldung, setDialogMeldung] = useState<{
    severity: 'success' | 'warning' | 'error';
    text: string;
  } | null>(null);
  // Die Meldung steht oben im Dialog — wer unten an der Schlagzeile speichert, sah sie nicht (Browser-
  // Prüfung 28.10.: 301 px außer Sicht). Jede neue Meldung rollt deshalb ins Bild.
  const meldungRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (dialogMeldung) meldungRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [dialogMeldung]);
  // Dirty-Riegel. „Speichern & bestätigen" speichert nur, wenn etwas geändert ist (sonst bestätigt
  // es direkt) — jede Feldänderung MUSS deshalb `setDialogDirty(true)` setzen, sonst bestätigt der
  // Knopf den alten gespeicherten Stand und die Änderung geht beim Schließen verloren
  // (.claude/rules/react-frontend.md „Ein neues Feld in einem Dialog erbt dessen Riegel NICHT").
  // Reset beim Öffnen und nach erfolgreichem Speichern.
  const [dialogDirty, setDialogDirty] = useState(false);
  // Export-Sperre. Bewusst NICHT Teil des Formulars und NICHT im dialogDirty-
  // Guard: sie wird sofort per eigenem Endpoint geschrieben, damit sie nicht
  // an einem vergessenen "Speichern" hängen bleibt. Eine Compliance-Sperre
  // darf nicht davon abhängen, dass jemand danach noch einen Button drückt.
  const [dialogExportBlocked, setDialogExportBlocked] = useState(false);
  const [exportBlockBusy, setExportBlockBusy] = useState(false);

  // Rückfragen (GefahrBestaetigung) — Wirkung in Klartext VOR der Aktion.
  const [absagenOffen, setAbsagenOffen] = useState(false);
  const [absagenFehler, setAbsagenFehler] = useState<string | null>(null);
  const [loeschenOffen, setLoeschenOffen] = useState(false);
  const [loeschenFehler, setLoeschenFehler] = useState<string | null>(null);
  const [exportSperreOffen, setExportSperreOffen] = useState(false);
  /** Ersetzen-Rückfrage offen; der Wert sagt, welcher Knopf danach weiterläuft. */
  const [ersetzenNach, setErsetzenNach] = useState<'speichern' | 'bestaetigen' | null>(null);

  // Moderator-Picker (Pflicht). Liste kommt aus dashboard_allowed_emails über
  // /admin/available-hosts, seit Paket 2 mit Anzeigenamen.
  const [dialogHostEmail, setDialogHostEmail] = useState<string>('');
  const [availableHosts, setAvailableHosts] = useState<AvailableHost[]>([]);
  const [availableHostsLoaded, setAvailableHostsLoaded] = useState(false);

  // Inline-Validation: leerer Moderator / leere Topics blockt Submit.
  const [dialogHostError, setDialogHostError] = useState<string | null>(null);
  const [dialogTopicsError, setDialogTopicsError] = useState<string | null>(null);

  // Newsletter-Schlagzeile des Sendetags (B7, vorher eigene Karte in „Heute“). Prefill-Riegel wie
  // bei den Werbevideo-Feldern: nur senden, wenn der Vorab-Abruf geklappt hat — sonst würde ein
  // gehusteter GET die gespeicherte Schlagzeile mit '' überschreiben.
  const [dialogHeadline, setDialogHeadline] = useState('');
  const [dialogHeadlineSaved, setDialogHeadlineSaved] = useState<string | null>(null);
  const [dialogHeadlineMax, setDialogHeadlineMax] = useState(200);
  const [dialogHeadlinePrefillOk, setDialogHeadlinePrefillOk] = useState(false);

  // Test-Link (D7, nur aus der adminAuth-Antwort von /admin/status) — für den Test-Hinweis.
  const [testLink, setTestLink] = useState<{
    stand: 'laedt' | 'da' | 'fehlt' | 'fehler';
    url?: string;
  }>({ stand: 'laedt' });

  // Werbevideo nach der Sendung — Per-Show-Wahl.
  // Sentinel-Strings: 'default' = Standard/Automatik, 'no-roll' = ausdrücklich keins,
  // Zahl = dieses Video.
  type AdVideoMode = 'default' | 'no-roll' | number;
  interface AdVideoLibRow {
    id: number;
    title: string;
    duration_sec: number | null;
    is_default: number;
    is_active: number;
    status: string;
    /** 'ad' = Werbeblock nach der Sendung, 'show' = Aufzeichnung als Sendung. */
    kind?: 'ad' | 'show';
    /** Vom Wiederholungs-Abgleich angelegt — nicht einzeln waehlbar, nur ueber „Automatisch". */
    managed_source_webinar_id?: string | null;
    rerun_current_for?: string | null;
    /** Zahl der Knöpfe dieses Videos (`live_ad_video_ctas`, ein- UND ausgeschaltete), aus GET /admin/ad-videos. */
    cta_count?: number | string;
    /** Wird ein Knopf sichtbar? Aus GET /admin/ad-videos. */
    angebot_lage?: AngebotLage | null;
  }
  const [dialogAdVideoMode, setDialogAdVideoMode] = useState<AdVideoMode>('default');
  // Angebot festlegen: aus dem Hinweis unter der Auswahl, oder weil der Server das Speichern abgelehnt hat.
  // `aufloesen` setzt dann das wartende Speichern fort.
  const [angebotFrage, setAngebotFrage] = useState<{
    video: { id: number; title: string };
    meldung?: string;
    aufloesen?: (ergebnis: AngebotErgebnis) => void;
  } | null>(null);
  // Was „Standard" an diesem Tag spielt: die Wiederholung (Schalter an, Zeiger fuer den Tag
  // gesetzt), sonst das Standard-Video. Nur Anzeige — gespeichert wird weiterhin explicit=0.
  const [dialogAutoRoll, setDialogAutoRoll] = useState<{
    rerunAn: boolean;
    rerunTitel: string | null;
    standardTitel: string | null;
  }>({ rerunAn: false, rerunTitel: null, standardTitel: null });
  const [dialogAdVideoLib, setDialogAdVideoLib] = useState<AdVideoLibRow[]>([]);
  const [dialogAdVideoPrefillOk, setDialogAdVideoPrefillOk] = useState(false);
  // Termin-Verlauf: wer hat wann was geaendert. Rein informativ, nie Teil des
  // Speicherns — deshalb auch kein dialogDirty.
  const [dialogHistory, setDialogHistory] = useState<OverrideChange[]>([]);

  // Sendungsart. 'live' = Signal aus dem Studio, 'recorded' = ein Video aus der Bibliothek läuft
  // zur geplanten Zeit als Sendung. Bewusst getrennt vom Werbevideo-Picker: der wählt die
  // WERBUNG NACH der Sendung.
  const [dialogBroadcastSource, setDialogBroadcastSource] = useState<'live' | 'recorded'>('live');
  const [dialogRecordedVideoId, setDialogRecordedVideoId] = useState<number | ''>('');
  const [dialogShowVideoLib, setDialogShowVideoLib] = useState<AdVideoLibRow[]>([]);
  const [dialogRecordedError, setDialogRecordedError] = useState<string | null>(null);

  // Themen ("Heute im Stream") — eine Zeile pro Thema, max. 5. Server verlangt min. 1 für jede
  // Termin-Art (live, Aufzeichnung, Test: `requiresHostAndTopics = action !== 'cancel'`).
  const [dialogTopicItems, setDialogTopicItems] = useState<string[]>(['']);
  // Prefill-Riegel der Themen (wie bei Werbevideo/Schlagzeile): Themen werden IMMER mitgeschickt und
  // ersetzen die gespeicherten. Ist der Vorab-Abruf gescheitert, stünde im Feld nur der Startwert — ein
  // Speichern mit frisch getippten Themen überschriebe dann still die gespeicherten. Deshalb: während
  // des Ladens gesperrt, nach einem Fehler kein Speichern. `gespeichert` = Anzahl der Themen auf dem
  // Server (für „Themen fehlen“ und den Direkt-Bestätigen-Weg).
  const [dialogTopicsStand, setDialogTopicsStand] = useState<
    { stand: 'laedt' } | { stand: 'ok'; gespeichert: number } | { stand: 'fehler' }
  >({ stand: 'laedt' });

  const calendarRef = useRef<FullCalendar | null>(null);

  // Handy (< 600 px, Persona-Runde 2): Termin-Dialog bildschirmfüllend, Status-Chips brechen um,
  // Schließen als ✕ im Kopf, Fuß in einer Zeile. Desktop bleibt unverändert.
  const theme = useTheme();
  const istHandy = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchSchedule = useCallback(async (from: string, to: string) => {
    setLoading(true);
    try {
      const res = await api.get<{
        success: boolean;
        shows: LiveShow[];
        holidays: HolidayInfo[];
      }>('/api/live/admin/schedule', { params: { from, to } });
      setShows(res.data.shows || []);
      setHolidays(res.data.holidays || []);
    } catch (err) {
      setToast({
        msg: fehlerText(err, 'Sendeplan konnte nicht geladen werden'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Refetch the visible range
  const refetch = useCallback(() => {
    if (visibleRangeRef.current) {
      fetchSchedule(visibleRangeRef.current.from, visibleRangeRef.current.to);
    }
  }, [fetchSchedule]);

  const events = useMemo<EventInput[]>(() => {
    const items: EventInput[] = [];
    const heute = moment.tz('Europe/Berlin').format('YYYY-MM-DD');
    for (const s of shows) {
      const kat = kategorieVon(s);
      const c = KATEGORIE[kat];
      // Kurz und ohne Abschneiden (C2): „10:00 Live", „10:00 Aufzeichnung", „10:00 Test".
      // Der Status steht zusätzlich als Wort da — die Farbe allein reicht nicht.
      const art =
        s.source === 'test' ? 'Test' : s.broadcast_source === 'recorded' ? 'Aufzeichnung' : 'Live';
      const teile = [`${s.time} ${art}`];
      if (s.source === 'reschedule') teile.push('(verschoben)');
      if (kat === 'offen') teile.push('– unbestätigt');
      if (kat === 'abgesagt') teile.push('– abgesagt');
      if (s.label) teile.push('· ' + s.label);
      // Export-Sperre im Kalender sichtbar machen — sonst sieht man erst beim
      // Öffnen des Tages, ob sie gesetzt wurde.
      if (s.ai_export_blocked) teile.push('· ohne YouTube-Video');
      // Pflichtfeld sichtbar machen, bevor jemand den Tag öffnet — nur für kommende echte Sendungen.
      if (s.source !== 'test' && kat !== 'abgesagt' && s.date >= heute && s.topic_count === 0) {
        teile.push('· Themen fehlen');
      }
      items.push({
        id: `${s.date}-${s.source}`,
        title: teile.join(' '),
        start: s.iso.replace(' ', 'T'),
        allDay: false,
        backgroundColor: c.bg,
        borderColor: c.border,
        textColor: c.text,
        classNames: kat === 'abgesagt' ? ['termin-abgesagt'] : [],
        extendedProps: { source: s.source, raw: s },
      });
    }
    for (const h of holidays) {
      items.push({
        id: `holiday-${h.date}`,
        title: 'Feiertag: ' + h.name,
        start: h.date,
        allDay: true,
        backgroundColor: HOLIDAY_COLOR.bg,
        borderColor: HOLIDAY_COLOR.border,
        textColor: HOLIDAY_COLOR.text,
        display: 'block',
        extendedProps: { holiday: true },
      });
    }
    return items;
  }, [shows, holidays]);

  /**
   * `vorhanden`: der Termin des Tages, frisch gelesen (Sprung aus „Heute“) — `null` = an dem Tag ist
   * nichts eingetragen. Ohne Angabe gilt der geladene Kalenderausschnitt.
   */
  const openDialogForDate = (dateStr: string, vorhanden?: LiveShow | null) => {
    const todayStr = moment().format('YYYY-MM-DD');
    if (dateStr < todayStr) return; // ignore past clicks

    const existing =
      vorhanden !== undefined ? (vorhanden ?? undefined) : shows.find((s) => s.date === dateStr);

    setDialogDate(dateStr);
    setDialogLabel(existing?.label && existing.source !== 'test' ? existing.label : '');
    setDialogNotes('');
    setDialogHostEmail(existing?.host_email ?? '');
    setDialogHostError(null);
    setDialogTopicsError(null);
    setDialogRecordedError(null);
    setDialogExistingSource(existing?.source ?? null);
    setDialogExistingStatus(existing?.status ?? null);
    setDialogCancelReason(existing?.cancel_reason ?? null);
    setDialogBestand(existing ? { source: existing.source, time: existing.time } : null);
    setDialogExportBlocked(Boolean(existing?.ai_export_blocked));
    setDialogMeldung(null);
    setAbsagenFehler(null);
    setLoeschenFehler(null);
    // Dirty-guard reset on open — the pre-fill from `existing` isn't a user edit.
    setDialogDirty(false);
    // Themen-Stand des VORIGEN Tages sofort verwerfen (sonst stünde bis zum Abruf dessen „Themen fehlen“).
    setDialogTopicsStand({ stand: 'laedt' });

    // Manual-Only-Modus: Dialog kennt nur noch 'extra' / 'test'. Existierende
    // Test-Shows bleiben 'test', alles andere (inkl. Legacy 'default' /
    // 'reschedule' / 'cancel') wird beim Bearbeiten zu 'extra' migriert.
    setDialogAction(existing?.source === 'test' ? 'test' : 'extra');
    setDialogTime(existing ? existing.time : standardUhrzeit(dateStr));
    setDialogOpen(true);
  };

  const handleDateClick = (arg: DateClickArg) => {
    openDialogForDate(arg.dateStr);
  };

  const handleEventClick = (arg: EventClickArg) => {
    arg.jsEvent.preventDefault();
    const dateStr = arg.event.startStr.slice(0, 10);
    if (dateStr) openDialogForDate(dateStr);
  };

  const dialogSchliessen = () => {
    if (dialogBusy || dialogDeleting || dialogConfirming || dialogCancelling) return;
    if (dialogDirty && !window.confirm('Nicht gespeicherte Änderungen verwerfen?')) return;
    setDialogOpen(false);
  };

  // Sprung aus „Heute“ (Schnittstelle mit index.tsx): Termin-Dialog des Tages öffnen wie ein Klick auf
  // den Tag. Der Termin wird dafür FRISCH gelesen — der geladene Kalenderausschnitt kann den Tag noch
  // nicht enthalten (Abruf läuft) oder veraltet sein. Funktionen über Refs, damit der Effekt nur am
  // Datum hängt (sonst liefe er bei jedem Render neu).
  const oeffneRef = useRef(openDialogForDate);
  oeffneRef.current = openDialogForDate;
  const erledigtRef = useRef(onOeffneDatumErledigt);
  erledigtRef.current = onOeffneDatumErledigt;
  const offenDirtyRef = useRef(false);
  offenDirtyRef.current = dialogOpen && dialogDirty;
  useEffect(() => {
    if (!oeffneDatum || !/^\d{4}-\d{2}-\d{2}$/.test(oeffneDatum)) return;
    let cancelled = false;
    (async () => {
      let vorhanden: LiveShow | null | undefined;
      try {
        const res = await api.get<{ success: boolean; shows: LiveShow[] }>(
          '/api/live/admin/schedule',
          { params: { from: oeffneDatum, to: oeffneDatum } },
        );
        vorhanden = (res.data.shows || []).find((s) => s.date === oeffneDatum) ?? null;
      } catch (err) {
        vorhanden = undefined;
        if (!cancelled) {
          setToast({
            msg:
              fehlerText(err, 'Termin konnte nicht geladen werden') +
              ' — bitte im Kalender auf den Tag klicken.',
            severity: 'error',
          });
        }
      }
      if (cancelled) return;
      try {
        calendarRef.current?.getApi().gotoDate(oeffneDatum);
      } catch {
        // Kalender noch nicht bereit — der Dialog öffnet trotzdem.
      }
      const verwerfenOk =
        !offenDirtyRef.current || window.confirm('Nicht gespeicherte Änderungen verwerfen?');
      if (vorhanden !== undefined && verwerfenOk) oeffneRef.current(oeffneDatum, vorhanden);
      erledigtRef.current?.();
    })();
    return () => {
      cancelled = true;
    };
  }, [oeffneDatum]);

  // Sofort-Schreibvorgang, unabhängig vom Formular-Speichern (siehe State-
  // Kommentar). Nur das Setzen wird bestätigt — das Aufheben ist der harmlose
  // Weg zurück in den Normalzustand.
  const schreibeExportSperre = async (blocked: boolean) => {
    if (!dialogDate) return;
    setExportBlockBusy(true);
    try {
      if (blocked) {
        await api.post('/api/live/admin/schedule/export-block', { date: dialogDate });
      } else {
        await api.delete('/api/live/admin/schedule/export-block', {
          data: { date: dialogDate },
        });
      }
      setDialogExportBlocked(blocked);
      setToast({
        msg: blocked
          ? 'Gesperrt: kein YouTube-Video nach der Sendung, kein Artikel.'
          : 'Sperre aufgehoben: nach der Sendung entstehen wieder YouTube-Video und Artikel.',
        severity: 'success',
      });
      refetch();
    } catch (err) {
      setToast({ msg: fehlerText(err, 'YouTube-Sperre fehlgeschlagen'), severity: 'error' });
    } finally {
      setExportBlockBusy(false);
      setExportSperreOffen(false);
    }
  };

  // Ersetzt das Speichern einen anderen echten Termin dieses Tages? (B9) Ein Test, der ersetzt
  // wird, fragt der Server nicht nach (`fremdEcht` in POST /admin/schedule/override) — hier auch
  // nur ein Hinweis, keine Rückfrage.
  const ersetztEchtenTermin =
    dialogBestand !== null &&
    dialogBestand.source !== dialogAction &&
    dialogBestand.source !== 'test';
  const ersetztTest = dialogBestand?.source === 'test' && dialogAction !== 'test';

  /**
   * Speichert den Termin (+ Themen, Werbevideo, Sendungsart, Schlagzeile). Rückgabe true = der
   * Termin ist gespeichert. `ersetzenGesehen`: die Ersetzen-Rückfrage wurde schon beantwortet —
   * passt die 409 des Servers genau zu dem Termin, den sie genannt hat, wird nicht noch einmal
   * gefragt; weicht sie ab (Stand hat sich geändert), fragt der Browser mit der Serverliste.
   */
  const speichern = async (opts: { ersetzenGesehen?: boolean } = {}): Promise<boolean> => {
    if (!dialogDate) return false;

    // Prefill-Riegel Themen (siehe State): ohne geladenen Stand würde das Speichern die gespeicherten
    // Themen mit dem Feldinhalt überschreiben.
    if (dialogTopicsStand.stand !== 'ok') {
      setDialogMeldung({
        severity: 'error',
        text:
          dialogTopicsStand.stand === 'laedt'
            ? 'Nicht gespeichert — die Themen werden noch geladen. Bitte einen Moment warten.'
            : 'Nicht gespeichert — die gespeicherten Themen konnten nicht geladen werden. Bitte das Fenster schließen und den Tag neu öffnen.',
      });
      return false;
    }
    // Prefill-Riegel Werbevideo/Sendungsart: der Server setzt FEHLENDE Felder per ODKU auf Standard
    // zurück (ad_video_explicit=false, broadcast_source='live' — POST /admin/schedule/override). Ohne
    // geladenen Stand würde Speichern also eine geplante Aufzeichnung oder eine Werbevideo-Wahl löschen.
    if (!dialogAdVideoPrefillOk) {
      setDialogMeldung({
        severity: 'error',
        text: 'Nicht gespeichert — Werbevideo und Sendungsart sind noch nicht geladen. Bitte einen Moment warten; klappt es nicht, das Fenster schließen und den Tag neu öffnen.',
      });
      return false;
    }
    // Uhrzeit frei (24-Stunden-Format); der Server verlangt HH:MM (routes/live.ts, POST /admin/schedule/override).
    if (!/^\d{2}:\d{2}$/.test(dialogTime.slice(0, 5))) {
      setDialogMeldung({
        severity: 'error',
        text: 'Nicht gespeichert — bitte eine Uhrzeit eintragen (z. B. 10:00 oder 15:30).',
      });
      return false;
    }

    // Inline-Validation: Moderator + Themen sind Pflicht. Server validiert
    // ebenfalls (defense in depth) — die Client-Checks vermeiden den Roundtrip.
    const hostEmailTrimmed = dialogHostEmail.trim().toLowerCase();
    const topicsArr = dialogTopicItems
      .map((t) => t.trim())
      .filter((t) => t.length > 0)
      .slice(0, 5);

    let hasError = false;
    if (!hostEmailTrimmed) {
      setDialogHostError('Bitte auswählen, wer moderiert.');
      hasError = true;
    } else {
      setDialogHostError(null);
    }
    if (topicsArr.length === 0) {
      setDialogTopicsError('Bitte mindestens ein Thema eintragen.');
      hasError = true;
    } else {
      setDialogTopicsError(null);
    }
    // Eine Aufzeichnung ohne Video ist kein sendbarer Termin — der Server
    // lehnt sie ohnehin mit `recorded_video_id_required` ab; hier fangen wir
    // sie am Feld ab, damit die Meldung dort steht, wo der Fehler ist.
    if (dialogBroadcastSource === 'recorded' && !dialogRecordedVideoId) {
      setDialogRecordedError('Bitte ein Video auswählen.');
      hasError = true;
    } else {
      setDialogRecordedError(null);
    }
    if (hasError) {
      setDialogMeldung({
        severity: 'error',
        text: 'Nicht gespeichert — bitte die rot markierten Pflichtfelder ausfüllen.',
      });
      return false;
    }

    setDialogBusy(true);
    setDialogMeldung(null);
    try {
      const body: Record<string, unknown> = {
        date: dialogDate,
        action: dialogAction,
        time: dialogTime.slice(0, 5),
        label: dialogLabel.trim() || null,
        notes: dialogNotes.trim() || null,
        host_email: hostEmailTrimmed,
        topics: topicsArr,
      };
      // Werbevideo-Felder: ohne geglückten Prefill kommt speichern() gar nicht bis hier (Riegel oben).
      if (dialogAdVideoPrefillOk) {
        if (dialogAdVideoMode === 'default') {
          body.ad_video_explicit = false;
          body.ad_video_id = null;
        } else if (dialogAdVideoMode === 'no-roll') {
          body.ad_video_explicit = true;
          body.ad_video_id = null;
        } else {
          body.ad_video_explicit = true;
          body.ad_video_id = dialogAdVideoMode;
        }
      }
      // Sendungsart — an denselben Prefill-Riegel gebunden wie die Werbevideo-
      // Felder darüber, und aus demselben Grund: schlägt der Vorab-Abruf fehl,
      // steht `dialogBroadcastSource` noch auf dem Startwert 'live'. Würden wir
      // ihn dann mitschicken, machte ein einzelner gehusteter GET aus einer
      // geplanten Aufzeichnung still wieder eine Live-Sendung — und die fällt
      // aus, weil niemand sendet.
      //
      // Innerhalb des Riegels wird 'live' bewusst MITgeschickt: sonst bliebe
      // beim Zurückstellen einer Aufzeichnung die alte Video-Referenz stehen.
      if (dialogAdVideoPrefillOk) {
        if (dialogBroadcastSource === 'recorded') {
          body.broadcast_source = 'recorded';
          body.recorded_video_id = dialogRecordedVideoId;
        } else {
          body.broadcast_source = 'live';
          body.recorded_video_id = null;
        }
      }
      // Override + Topics werden vom Server in einer Transaktion geschrieben.
      // Die Zeitfenster-CTAs (POST /admin/ctas) schreibt dieser Dialog seit Paket 2 NICHT mehr
      // (Till Q2, 28.10.): die Oberfläche dafür ist weg, und ein Mitschicken der leeren Liste
      // hätte bestehende Zeilen des Tages still gelöscht.
      //
      // C2 (P3): ersetzt das Speichern einen anderen Termin desselben Tages, antwortet der Server
      // 409 `ersetzt_termin` mit dem aktuellen Stand. Erneut gespeichert wird nur mit genau der
      // gesehenen Liste in `ersetzt`. Hat sich der Stand inzwischen geändert, kommt die nächste
      // 409 mit dem neuen Stand und eine neue Frage.
      for (let versuch = 0; ; versuch++) {
        try {
          await api.post('/api/live/admin/schedule/override', body);
          break;
        } catch (err) {
          const data = (
            err as {
              response?: {
                data?: {
                  error?: string;
                  details?: Array<{ action: string; scheduled_time: string | null }>;
                };
              };
            }
          )?.response?.data;
          // Video ohne Angebot: erst klären, dann speichert derselbe Aufruf durch.
          const ohneAngebot = data as
            | { error?: string; details?: unknown; video?: { id: number; title: string } }
            | undefined;
          if (ohneAngebot?.error === 'ohne_angebot' && ohneAngebot.video) {
            const video = ohneAngebot.video;
            const ergebnis = await new Promise<AngebotErgebnis>((aufloesen) => {
              setAngebotFrage({
                video,
                meldung: typeof ohneAngebot.details === 'string' ? ohneAngebot.details : undefined,
                aufloesen,
              });
            });
            if (ergebnis === 'abgebrochen') {
              setDialogMeldung({
                severity: 'warning',
                text: 'Nicht gespeichert: Das gewählte Werbevideo hat kein Angebot.',
              });
              return false;
            }
            continue;
          }
          if (data?.error !== 'ersetzt_termin' || !Array.isArray(data.details) || versuch >= 3) {
            throw err;
          }
          const details = data.details;
          const passtZurRueckfrage =
            opts.ersetzenGesehen === true &&
            versuch === 0 &&
            dialogBestand !== null &&
            details.length === 1 &&
            details[0].action === dialogBestand.source &&
            String(details[0].scheduled_time ?? '').slice(0, 5) === dialogBestand.time.slice(0, 5);
          if (!passtZurRueckfrage) {
            const liste = details
              .map(
                (r) =>
                  `– ${AKTION_TEXT[r.action] || r.action}${r.scheduled_time ? ` um ${r.scheduled_time.slice(0, 5)} Uhr` : ''}`,
              )
              .join('\n');
            if (
              !window.confirm(
                `An diesem Tag ist inzwischen eingetragen:\n${liste}\n\nBeim Speichern wird dieser Termin gelöscht und durch ${dialogAction === 'test' ? 'den Test' : 'diese Sendung'} ersetzt. Fortfahren?`,
              )
            ) {
              setDialogMeldung({
                severity: 'warning',
                text: 'Nicht gespeichert — der bestehende Termin bleibt.',
              });
              return false;
            }
          }
          body.ersetzt = details;
        }
      }

      // Schlagzeile (B7): eigener Endpunkt, nach dem Termin. Nur für echte Sendungen (ein Test
      // erscheint nie im Newsletter), nur nach geglücktem Vorab-Abruf und nur bei Änderung.
      let schlagzeileFehler = false;
      if (
        dialogAction === 'extra' &&
        dialogHeadlinePrefillOk &&
        (dialogHeadline.trim() || null) !== dialogHeadlineSaved
      ) {
        try {
          const behalten = await speichereSchlagzeile(dialogDate, dialogHeadline);
          setDialogHeadline(behalten ?? '');
          setDialogHeadlineSaved(behalten);
        } catch {
          schlagzeileFehler = true;
        }
      }

      // Lokalen Stand nachziehen: der Dialog kann offen bleiben (A2).
      // Status: der Server lässt ihn beim Ändern derselben Termin-Art stehen (ODKU ohne `status`);
      // eine neue Zeile (neuer Termin, Art gewechselt, alter Sendeplan ersetzt) startet 'pending'.
      const gleicheArt = dialogExistingSource === dialogAction;
      setDialogExistingSource(dialogAction);
      setDialogExistingStatus(
        dialogAction === 'test' ? 'confirmed' : gleicheArt ? dialogExistingStatus : 'pending',
      );
      if (!gleicheArt) setDialogCancelReason(null);
      setDialogBestand({ source: dialogAction, time: dialogTime.slice(0, 5) });
      setDialogTopicsStand({ stand: 'ok', gespeichert: topicsArr.length });
      refetch();
      if (schlagzeileFehler) {
        // Teilerfolg ist kein Erfolg: Dialog bleibt offen und „ungespeichert", damit der nächste
        // Klick den Schlagzeilen-PUT wiederholt (und „Speichern & bestätigen" nicht still bestätigt).
        setDialogDirty(true);
        setDialogMeldung({
          severity: 'error',
          text: 'Termin gespeichert, aber die Newsletter-Schlagzeile NICHT — bitte noch einmal speichern.',
        });
        return false;
      }
      setDialogDirty(false);
      return true;
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      const code = e?.response?.data?.error;
      // Server-Validation-Codes auf inline-Fehler mappen, sonst Meldung im Dialog.
      if (code === 'host_email_required' || code === 'host_email_invalid') {
        setDialogHostError(
          code === 'host_email_invalid'
            ? 'Diese Person ist nicht mehr freigeschaltet — bitte jemand anderen wählen.'
            : 'Bitte auswählen, wer moderiert.',
        );
        setDialogMeldung({ severity: 'error', text: 'Nicht gespeichert — Moderation fehlt.' });
      } else if (code === 'topics_required' || code === 'topics_invalid') {
        setDialogTopicsError('Bitte mindestens ein Thema eintragen.');
        setDialogMeldung({ severity: 'error', text: 'Nicht gespeichert — Themen fehlen.' });
      } else {
        setDialogMeldung({
          severity: 'error',
          text: 'Nicht gespeichert — ' + fehlerText(err, 'Speichern fehlgeschlagen'),
        });
      }
      return false;
    } finally {
      setDialogBusy(false);
    }
  };

  // Bestätigen über die bestehende Route (nur für die bestätigungspflichtigen Arten extra/reschedule).
  const bestaetigen = async (
    action: string,
  ): Promise<{ ok: true } | { ok: false; grund: string }> => {
    setDialogConfirming(true);
    try {
      await api.post('/api/live/admin/schedule/confirm', { show_date: dialogDate, action });
      setDialogExistingStatus('confirmed');
      setDialogCancelReason(null);
      refetch();
      return { ok: true };
    } catch (err) {
      return { ok: false, grund: fehlerText(err, 'Bestätigung fehlgeschlagen') };
    } finally {
      setDialogConfirming(false);
    }
  };

  /** A1: EIN Knopf für unbestätigte (und abgesagte) echte Sendungen — speichert, dann bestätigt. */
  const handleSpeichernUndBestaetigen = async (opts: { ersetzenGesehen?: boolean } = {}) => {
    const gated = dialogExistingSource === 'extra' || dialogExistingSource === 'reschedule';
    let action = 'extra';
    let gespeichert = false;
    // Direkt bestätigen nur, wenn der gespeicherte Stand vollständig ist: ein Bestandstermin ohne Themen
    // läuft durch `speichern()` und damit durch die Pflichtprüfung (Persona-Runde 1, Felix: „bestätigt
    // leere Sendungen“).
    const gespeicherteThemen = dialogTopicsStand.stand === 'ok' ? dialogTopicsStand.gespeichert : 0;
    if (!dialogDirty && gated && dialogExistingSource && gespeicherteThemen > 0) {
      // Nichts geändert: der gespeicherte Stand IST, was der Dialog zeigt — direkt bestätigen.
      action = dialogExistingSource;
    } else {
      if (ersetztEchtenTermin && !opts.ersetzenGesehen) {
        setErsetzenNach('bestaetigen');
        return;
      }
      if (!(await speichern(opts))) return;
      gespeichert = true;
    }
    const r = await bestaetigen(action);
    if (r.ok) {
      setToast({
        msg: gespeichert ? 'Gespeichert und bestätigt.' : 'Sendung bestätigt.',
        severity: 'success',
      });
      setDialogOpen(false);
      return;
    }
    setDialogMeldung({
      severity: 'error',
      text:
        (gespeichert ? 'Gespeichert, aber NICHT bestätigt — ' : 'NICHT bestätigt — ') +
        r.grund +
        `. Bitte noch einmal auf „Speichern & bestätigen“ klicken; sonst wird die Sendung am Sendetag um ${BESTAETIGEN_BIS} Uhr automatisch abgesagt.`,
    });
  };

  /** Speichern ohne Bestätigen. Bei unbestätigter Sendung bleibt der Dialog offen und sagt es. */
  const handleNurSpeichern = async (opts: { ersetzenGesehen?: boolean } = {}) => {
    if (ersetztEchtenTermin && !opts.ersetzenGesehen) {
      setErsetzenNach('speichern');
      return;
    }
    const gleicheArt = dialogExistingSource === dialogAction;
    const statusNachher =
      dialogAction === 'test'
        ? 'test'
        : gleicheArt
          ? (dialogExistingStatus ?? 'pending')
          : 'pending';
    if (!(await speichern(opts))) return;
    if (statusNachher === 'test') {
      setToast({ msg: 'Test gespeichert.', severity: 'success' });
      setDialogOpen(false);
    } else if (statusNachher === 'confirmed') {
      setToast({ msg: 'Gespeichert — die Sendung bleibt bestätigt.', severity: 'success' });
      setDialogOpen(false);
    } else if (statusNachher === 'cancelled') {
      setToast({ msg: 'Gespeichert — die Sendung bleibt abgesagt.', severity: 'success' });
      setDialogOpen(false);
    } else {
      setDialogMeldung({
        severity: 'warning',
        text: `Gespeichert. Die Sendung ist aber noch NICHT bestätigt: Zuschauer sehen sie nicht, und ohne Bestätigung wird sie am Sendetag um ${BESTAETIGEN_BIS} Uhr automatisch abgesagt.`,
      });
    }
  };

  const handleCancelShow = async () => {
    if (!dialogDate || !dialogExistingSource) return;
    setDialogCancelling(true);
    setAbsagenFehler(null);
    try {
      await api.post('/api/live/admin/schedule/cancel', {
        show_date: dialogDate,
        action: dialogExistingSource,
      });
      setToast({ msg: 'Sendung abgesagt.', severity: 'success' });
      setAbsagenOffen(false);
      setDialogOpen(false);
      refetch();
    } catch (err) {
      setAbsagenFehler(fehlerText(err, 'Absage fehlgeschlagen'));
    } finally {
      setDialogCancelling(false);
    }
  };

  const handleDelete = async () => {
    if (!dialogDate) return;
    setDialogDeleting(true);
    setLoeschenFehler(null);
    try {
      await api.delete('/api/live/admin/schedule/override', {
        data: { date: dialogDate },
      });
      setToast({ msg: 'Termin gelöscht.', severity: 'success' });
      setLoeschenOffen(false);
      setDialogOpen(false);
      refetch();
    } catch (err) {
      setLoeschenFehler(fehlerText(err, 'Löschen fehlgeschlagen'));
    } finally {
      setDialogDeleting(false);
    }
  };

  // Trigger initial fetch on mount with a 60-day window starting today
  useEffect(() => {
    const from = moment().format('YYYY-MM-DD');
    const to = moment().add(60, 'days').format('YYYY-MM-DD');
    visibleRangeRef.current = { from, to };
    fetchSchedule(from, to);
  }, [fetchSchedule]);

  // Moderator-Liste (dashboard_allowed_emails) bei jedem Dialog-Open refreshen, damit neu
  // hinzugefügte User direkt verfügbar sind ohne Page-Reload.
  useEffect(() => {
    if (!dialogOpen) return;
    let cancelled = false;
    api
      .get<{ success: boolean; data: { hosts: AvailableHost[] } }>(
        '/api/live/admin/available-hosts',
      )
      .then((res) => {
        if (cancelled) return;
        const hosts = [...(res.data.data?.hosts || [])].sort((a, b) =>
          (a.name || a.email).localeCompare(b.name || b.email, 'de'),
        );
        setAvailableHosts(hosts);
        setAvailableHostsLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setAvailableHosts([]);
        setAvailableHostsLoaded(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dialogOpen]);

  // Newsletter-Schlagzeile des Sendetags vorladen. Erst leeren, dann holen (Dialog wird nicht neu
  // gemountet — sonst stünde kurz die Schlagzeile des vorigen Tages im Feld).
  useEffect(() => {
    setDialogHeadline('');
    setDialogHeadlineSaved(null);
    setDialogHeadlinePrefillOk(false);
    if (!dialogOpen || !dialogDate) return;
    let cancelled = false;
    ladeSchlagzeile(dialogDate)
      .then((stand) => {
        if (cancelled) return;
        setDialogHeadline(stand.headline ?? '');
        setDialogHeadlineSaved(stand.headline);
        setDialogHeadlineMax(stand.maxLength);
        setDialogHeadlinePrefillOk(true);
      })
      .catch(() => {
        if (!cancelled) setDialogHeadlinePrefillOk(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dialogOpen, dialogDate]);

  // Test-Link nur laden, wenn der Dialog auf „Test" steht (einmal je Öffnen).
  useEffect(() => {
    if (!dialogOpen || dialogAction !== 'test') return;
    let cancelled = false;
    setTestLink({ stand: 'laedt' });
    api
      .get<{ admin_diag?: { test_vorschau_url?: string | null } }>('/api/live/admin/status')
      .then((res) => {
        if (cancelled) return;
        const url = res.data?.admin_diag?.test_vorschau_url;
        setTestLink(url ? { stand: 'da', url } : { stand: 'fehlt' });
      })
      .catch(() => {
        if (!cancelled) setTestLink({ stand: 'fehler' });
      });
    return () => {
      cancelled = true;
    };
  }, [dialogOpen, dialogAction]);

  // Termin-Verlauf laden. Eigener Effekt und fail-open: der Verlauf ist eine
  // Zusatzinformation und darf das Oeffnen des Dialogs nie blockieren.
  useEffect(() => {
    if (!dialogOpen || !dialogDate) {
      setDialogHistory([]);
      return;
    }
    let cancelled = false;
    api
      .get<{ success: boolean; changes: OverrideChange[] }>(
        '/api/live/admin/schedule/override/history',
        { params: { date: dialogDate } },
      )
      .then((res) => {
        if (!cancelled) setDialogHistory(res.data.changes || []);
      })
      .catch(() => {
        if (!cancelled) setDialogHistory([]);
      });
    return () => {
      cancelled = true;
    };
  }, [dialogOpen, dialogDate]);

  // Pre-fill ad-video selection + library when dialog opens. Two separate
  // GETs in parallel (library is shared across all dates, override is per-date).
  // Ohne erfolgreichen Override-GET wird der Mode auf 'default' belassen und
  // dialogAdVideoPrefillOk bleibt false → speichern() lehnt ab (der Server würde fehlende Felder
  // auf Standard zurücksetzen).
  useEffect(() => {
    if (!dialogOpen || !dialogDate) {
      setDialogAdVideoMode('default');
      setDialogAdVideoPrefillOk(false);
      return;
    }
    let cancelled = false;
    setDialogAdVideoPrefillOk(false);
    Promise.all([
      api.get<{ success: boolean; videos: AdVideoLibRow[]; autoroll_rerun_enabled?: boolean }>(
        '/api/live/admin/ad-videos',
      ),
      api.get<{
        success: boolean;
        overrides: Array<{
          ad_video_id: number | null;
          ad_video_explicit: number;
          broadcast_source?: string | null;
          recorded_video_id?: number | null;
        }>;
      }>('/api/live/admin/schedule/override', { params: { date: dialogDate } }),
    ])
      .then(([libRes, ovrRes]) => {
        if (cancelled) return;
        // Library auf usable Videos filtern (ready+active)
        const usable = (libRes.data.videos || []).filter(
          (v) => v.status === 'ready' && v.is_active,
        );
        // Zwei getrennte Listen aus einem Abruf: Werbeblöcke gehören in den
        // Werbevideo-Picker, Sendungen in den Aufzeichnungs-Picker. Ohne die
        // Trennung könnte man eine ganze Sendung als Werbung anhängen oder
        // umgekehrt — der Server lehnt beides ab, aber ein Picker, der
        // Unmögliches anbietet, ist eine Falle.
        // Verwaltete Zeilen (Wiederholungs-Abgleich) nicht einzeln anbieten: sie wechseln je
        // Quelle, eine feste Wahl zeigte nach dem naechsten Quellwechsel aufs alte Webinar.
        const lib = usable.filter(
          (v) => (v.kind ?? 'ad') === 'ad' && v.managed_source_webinar_id == null,
        );
        setDialogAdVideoLib(lib);
        const rerunZeile = usable.find(
          (v) =>
            v.managed_source_webinar_id != null &&
            String(v.rerun_current_for ?? '').slice(0, 10) === dialogDate,
        );
        setDialogAutoRoll({
          rerunAn: libRes.data.autoroll_rerun_enabled === true,
          rerunTitel: rerunZeile?.title ?? null,
          standardTitel: lib.find((v) => v.is_default)?.title ?? null,
        });
        setDialogShowVideoLib(usable.filter((v) => v.kind === 'show'));
        // Mode aus der zuletzt-aktualisierten Override-Row ableiten (egal welche
        // Action, egal welcher explicit-Wert) — Backend-Liste ist nach updated_at
        // DESC sortiert. „Letzter Edit gewinnt" ist konsistent mit der Backend-
        // Resolution.
        const latest = (ovrRes.data.overrides || [])[0];
        if (latest && latest.ad_video_explicit === 1) {
          if (latest.ad_video_id) {
            setDialogAdVideoMode(latest.ad_video_id);
          } else {
            setDialogAdVideoMode('no-roll');
          }
        } else {
          setDialogAdVideoMode('default');
        }
        // Sendungsart aus derselben jüngsten Zeile. Fehlt das Feld (alter
        // Server im Deploy-Fenster), bleibt es bei 'live'.
        if (latest?.broadcast_source === 'recorded' && latest.recorded_video_id) {
          setDialogBroadcastSource('recorded');
          setDialogRecordedVideoId(latest.recorded_video_id);
        } else {
          setDialogBroadcastSource('live');
          setDialogRecordedVideoId('');
        }
        setDialogAdVideoPrefillOk(true);
      })
      .catch(() => {
        if (cancelled) return;
        setDialogAdVideoLib([]);
        setDialogAutoRoll({ rerunAn: false, rerunTitel: null, standardTitel: null });
        setDialogAdVideoMode('default');
        setDialogAdVideoPrefillOk(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dialogOpen, dialogDate]);

  // Pre-fill topics when dialog opens for a specific date.
  useEffect(() => {
    if (!dialogOpen || !dialogDate) {
      setDialogTopicItems(['']);
      setDialogTopicsStand({ stand: 'laedt' });
      return;
    }
    let cancelled = false;
    setDialogTopicItems(['']);
    setDialogTopicsStand({ stand: 'laedt' });
    api
      .get<{ success: boolean; topics: string[] }>('/api/live/admin/topics', {
        params: { date: dialogDate },
      })
      .then((res) => {
        if (!cancelled) {
          const fetched = (res.data.topics || []).filter(
            (t) => typeof t === 'string' && t.trim().length > 0,
          );
          setDialogTopicItems(fetched.length > 0 ? fetched.slice(0, 5) : ['']);
          setDialogTopicsStand({ stand: 'ok', gespeichert: fetched.length });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDialogTopicItems(['']);
          setDialogTopicsStand({ stand: 'fehler' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [dialogOpen, dialogDate]);

  const dialogHoliday = holidays.find((h) => h.date === dialogDate);
  const dialogIsLegacyEdit =
    dialogExistingSource !== null &&
    dialogExistingSource !== 'extra' &&
    dialogExistingSource !== 'test';
  const istGated = dialogExistingSource === 'extra' || dialogExistingSource === 'reschedule';
  const arbeitet = dialogBusy || dialogDeleting || dialogConfirming || dialogCancelling;
  const datumKurz = dialogDate ? moment(dialogDate).format('DD.MM.') : '';

  // Heute nach 08:50: die Absage-Frist ist gelaufen (runCutoffCron feuert einmal um 08:50), eine danach
  // angelegte unbestätigte Sendung bleibt einfach unbestätigt. Der Satz „bis 08:50 bestätigen" wäre dann falsch.
  const fristHeuteVorbei =
    dialogDate === moment.tz('Europe/Berlin').format('YYYY-MM-DD') &&
    moment.tz('Europe/Berlin').format('HH:mm') >= BESTAETIGEN_BIS;

  // A2: Status im Dialogkopf — aus dem GESPEICHERTEN Stand, nicht aus dem Formular.
  const kopfStatus = ((): {
    text: string;
    farbe: 'success' | 'warning' | 'error' | 'default' | 'info';
  } => {
    if (!dialogExistingSource) return { text: 'Noch nicht angelegt', farbe: 'default' };
    if (dialogExistingSource === 'test') {
      return { text: 'Test (nur intern) — keine Bestätigung nötig', farbe: 'info' };
    }
    if (dialogExistingStatus === 'cancelled') {
      return {
        text:
          dialogCancelReason === 'auto_no_confirm'
            ? `Abgesagt — automatisch um ${BESTAETIGEN_BIS} Uhr, weil nicht bestätigt`
            : 'Abgesagt',
        farbe: 'error',
      };
    }
    if (dialogExistingStatus === 'pending') {
      return {
        text: fristHeuteVorbei
          ? 'Noch nicht bestätigt — angekündigt wird sie erst nach dem Bestätigen'
          : `Noch nicht bestätigt — bis ${BESTAETIGEN_BIS} Uhr am Sendetag bestätigen`,
        farbe: 'warning',
      };
    }
    return { text: 'Bestätigt', farbe: 'success' };
  })();

  // Welcher Hauptknopf? (A1)
  // Nur eine bestätigte 'extra'-Zeile bleibt beim Speichern bestätigt (ODKU ohne `status`); jeder
  // andere Stand (neu, Test → echt, alter Sendeplan) ergibt eine neue, unbestätigte Zeile.
  const zeigeBestaetigenKnopf =
    dialogAction === 'extra' &&
    dialogExistingStatus !== 'cancelled' &&
    !(dialogExistingSource === 'extra' && dialogExistingStatus === 'confirmed');
  const zeigeWiederBestaetigen =
    dialogAction === 'extra' && istGated && dialogExistingStatus === 'cancelled';

  const werbevideoStandardText = dialogAutoRoll.rerunAn
    ? dialogAutoRoll.rerunTitel
      ? `Automatisch (an diesem Tag: Wiederholung „${dialogAutoRoll.rerunTitel}“)`
      : `Automatisch (Wiederholung des Sendetags, sonst Standard „${dialogAutoRoll.standardTitel ?? 'nicht festgelegt'}“)`
    : dialogAutoRoll.standardTitel
      ? `Standard („${dialogAutoRoll.standardTitel}“) — folgt dem Standard, auch wenn er wechselt`
      : 'Standard (zurzeit ist kein Standard-Werbevideo festgelegt)';

  // Werbevideo-Auswahl (Persona-Runde 2, Felix): sagt, ob das Video einen Knopf (z. B. zur Bestellung)
  // trägt. `cta_count` zählt auch ausgeschaltete Knöpfe — daher „eingerichtet“, nicht „sichtbar“.
  const knopfText = (v: AdVideoLibRow): string => {
    if (v.cta_count == null) return '';
    const n = Number(v.cta_count) || 0;
    if (lageFehlt(v.angebot_lage)) return ' · kein Angebot';
    if (v.angebot_lage === 'bewusst_ohne') return ' · bewusst ohne Angebot';
    if (n === 0) return ' · ohne Knopf';
    return n === 1 ? ' · 1 Knopf eingerichtet' : ` · ${n} Knöpfe eingerichtet`;
  };
  // Was an diesem Tag nach der Sendung läuft, soweit die Auswahl es festlegt. Bei „Automatisch“ mit
  // Wiederholung steht das Video erst am Sendetag fest, dann prüft der Wächter.
  const gewaehltesVideo =
    typeof dialogAdVideoMode === 'number'
      ? dialogAdVideoLib.find((v) => v.id === dialogAdVideoMode)
      : dialogAdVideoMode === 'default' && !dialogAutoRoll.rerunTitel
        ? dialogAdVideoLib.find((v) => !!v.is_default)
        : undefined;
  const videoOhneAngebot = lageFehlt(gewaehltesVideo?.angebot_lage) ? gewaehltesVideo : undefined;
  const chipUmbruch = istHandy
    ? { height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }
    : undefined;

  const hostName = (h: AvailableHost) => h.name?.trim() || h.email;
  const doppelteNamen = new Set(
    availableHosts.map(hostName).filter((n, i, alle) => alle.indexOf(n) !== i),
  );

  const istTest = dialogAction === 'test';
  // Gespeicherter Termin (echt, nicht abgesagt) ohne ein einziges gespeichertes Thema — Bestand aus der Zeit,
  // bevor der Dialog Themen verlangte, oder per „Jetzt bestätigen“ ohne Themen bestätigt. Nur aus dem
  // GELADENEN Stand: solange der Abruf läuft oder scheiterte, wird nichts behauptet.
  const themenFehlen =
    dialogTopicsStand.stand === 'ok' &&
    dialogTopicsStand.gespeichert === 0 &&
    dialogExistingSource !== null &&
    dialogExistingSource !== 'test' &&
    dialogExistingStatus !== 'cancelled';
  const istHeute = dialogDate === moment.tz('Europe/Berlin').format('YYYY-MM-DD');
  // Satz zur Bestätigungs-Mail für die Absagen-Wirkung (Persona-Runde 2, Sandra). Belege an MODERATIONSMAIL_UM.
  const nachModerationsmail =
    istHeute && moment.tz('Europe/Berlin').format('HH:mm') >= MODERATIONSMAIL_UM;
  const absageMailSatz = !nachModerationsmail
    ? `Die Moderation bekommt um ${MODERATIONSMAIL_UM} Uhr keine Bestätigungs-Mail — die geht nur an noch nicht bestätigte Sendungen.`
    : dialogExistingStatus === 'pending'
      ? `Die Bestätigungs-Mail an die Moderation ist heute um ${MODERATIONSMAIL_UM} Uhr schon verschickt worden — bitte der Moderation Bescheid geben: ihr Link „Bestätigen“ würde die Sendung wieder ansetzen.`
      : `War die Sendung heute um ${MODERATIONSMAIL_UM} Uhr noch nicht bestätigt, hat die Moderation eine Bestätigungs-Mail bekommen — dann Bescheid geben: ihr Link „Bestätigen“ würde die Sendung wieder ansetzen.`;
  const zeigeAbsagen = istGated && dialogExistingStatus !== 'cancelled';
  // Anzeigename der Moderation für die Newsletter-Vorschau — dieselbe Quelle wie der Newsletter
  // (`available-hosts` liefert `LiveScheduleService.resolveModeratorName`).
  const gewaehlterHost = availableHosts.find((h) => h.email === dialogHostEmail);
  const vorschauModeration = dialogHostEmail ? gewaehlterHost?.name?.trim() || null : null;
  // Die Speichern-Knöpfe, die der Fuß gerade zeigt — wörtlich, für den Hinweis an der Schlagzeile.
  const speichernKnoepfe = zeigeBestaetigenKnopf
    ? '„Nur speichern“ oder „Speichern & bestätigen“'
    : zeigeWiederBestaetigen
      ? '„Speichern“ oder „Speichern & wieder ansetzen“'
      : '„Speichern“';

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" mb={1.5} sx={{ maxWidth: '100ch' }}>
        Hier wird jede Sendung geplant — nichts entsteht automatisch. Auf einen Tag klicken, um
        einen Termin anzulegen oder zu ändern — auch auf den heutigen Tag, etwa für einen Test
        gleich heute. Jede echte Sendung muss bis {BESTAETIGEN_BIS} Uhr am Sendetag bestätigt sein,
        sonst wird sie automatisch abgesagt.
      </Typography>

      {/* C1: Legende — die Farbe sagt den Status. */}
      <Box display="flex" flexWrap="wrap" gap={1.5} mb={1.5} alignItems="center">
        {(Object.keys(KATEGORIE) as Kategorie[]).map((k) => (
          <Box key={k} display="flex" alignItems="center" gap={0.75}>
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '3px',
                bgcolor: KATEGORIE[k].bg,
                border: `2px ${k === 'abgesagt' ? 'dashed' : 'solid'} ${KATEGORIE[k].border}`,
              }}
            />
            {/* Abgesagt wie im Kalender: rot und durchgestrichen. */}
            <Typography
              variant="caption"
              sx={
                k === 'abgesagt'
                  ? { color: KATEGORIE.abgesagt.text, textDecoration: 'line-through' }
                  : { color: '#374151' }
              }
            >
              {KATEGORIE[k].label}
            </Typography>
          </Box>
        ))}
        <Box display="flex" alignItems="center" gap={0.75}>
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: '3px',
              bgcolor: HOLIDAY_COLOR.bg,
              border: `2px solid ${HOLIDAY_COLOR.border}`,
            }}
          />
          <Typography variant="caption" sx={{ color: '#374151' }}>
            Feiertag
          </Typography>
        </Box>
        {/* Text-Markierung im Eintrag, keine Farbe (Kalender: `· Themen fehlen`). */}
        <Typography variant="caption" sx={{ color: '#374151' }}>
          Eintrag mit <strong>· Themen fehlen</strong>: für diese Sendung ist noch kein Thema
          gespeichert
        </Typography>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" my={2}>
          <CircularProgress size={24} />
        </Box>
      )}

      <Box
        sx={{
          backgroundColor: '#ffffff',
          borderRadius: 1,
          p: 1.5,
          '& .fc': { fontFamily: 'inherit' },
          '& .fc-toolbar-title': { fontSize: '1.1rem' },
          '& .fc-event': { cursor: 'pointer', fontSize: '0.78rem' },
          // C2: Einträge nie abschneiden — umbrechen statt „…".
          '& .fc-daygrid-event, & .fc-event-main, & .fc-event-title': {
            whiteSpace: 'normal',
            overflow: 'visible',
            textOverflow: 'clip',
          },
          '& .termin-abgesagt .fc-event-title': { textDecoration: 'line-through' },
          '& .fc-event.termin-abgesagt': { borderStyle: 'dashed' },
          '& .fc-day-past .fc-daygrid-day-number': { opacity: 0.5 },
        }}
      >
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          locale={deLocale}
          firstDay={1}
          weekends={true}
          height="auto"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: '',
          }}
          buttonText={{ today: 'Heute', month: 'Monat' }}
          events={events}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          datesSet={(arg) => {
            const from = moment(arg.start).format('YYYY-MM-DD');
            const to = moment(arg.end).format('YYYY-MM-DD');
            visibleRangeRef.current = { from, to };
            fetchSchedule(from, to);
          }}
          eventDisplay="block"
          displayEventTime={false}
        />
      </Box>

      {/* Termin-Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={dialogSchliessen}
        maxWidth="sm"
        fullWidth
        fullScreen={istHandy}
      >
        <DialogTitle sx={istHandy ? { pb: 1, pr: 1, pl: 2 } : { pb: 1 }}>
          <Box display="flex" alignItems="flex-start" gap={1}>
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ flex: 1 }}>
              <span>
                {dialogDate ? datumLang(dialogDate) : ''}
                {istHeute ? ' (heute)' : ''}
              </span>
              {/* Handy: Chip bricht um statt abzuschneiden (Persona-Runde 2, 390 px). */}
              <Chip
                size="small"
                color={kopfStatus.farbe}
                label={kopfStatus.text}
                sx={chipUmbruch}
              />
              {themenFehlen && (
                <Chip size="small" color="warning" label="Themen fehlen" sx={chipUmbruch} />
              )}
            </Box>
            {istHandy && (
              <IconButton
                onClick={dialogSchliessen}
                disabled={arbeitet}
                aria-label="Schließen"
                size="small"
                sx={{ mt: -0.25 }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={istHandy ? { px: 2 } : undefined}>
          {/* Absagen auch oben (Persona-Runde 2: stand nur ganz unten, unter der Themen-Warnung). Dieselbe
              Rückfrage wie der Knopf unten. */}
          {zeigeAbsagen && (
            <Box sx={{ mt: -0.5, mb: 1.5 }}>
              <Button
                size="small"
                color="error"
                onClick={() => {
                  setAbsagenFehler(null);
                  setAbsagenOffen(true);
                }}
                disabled={arbeitet}
                sx={{ px: 0.5, minWidth: 0 }}
              >
                Sendung absagen …
              </Button>
            </Box>
          )}
          {dialogMeldung && (
            <Alert
              ref={meldungRef}
              severity={dialogMeldung.severity}
              sx={{ mb: 2 }}
              onClose={() => setDialogMeldung(null)}
            >
              {dialogMeldung.text}
            </Alert>
          )}
          {dialogHoliday && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Feiertag: {dialogHoliday.name}.
            </Alert>
          )}
          {dialogIsLegacyEdit && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Dieser Termin stammt aus dem alten Sendeplan (
              {AKTION_TEXT[dialogExistingSource ?? '']}). Beim Speichern wird er durch einen
              normalen Termin ersetzt — der muss danach neu bestätigt werden.
            </Alert>
          )}

          {/* B4: Art des Termins — echt oder Test. Getrennt davon: was zur Sendezeit läuft. */}
          <Feld
            titel="Art des Termins"
            {...kz('pflicht')}
            wofuer="Eine echte Sendung sehen alle Zuschauer. Ein Test ist nur intern und dient zum Ausprobieren."
          >
            <FormControl component="fieldset">
              <RadioGroup
                row
                value={dialogAction}
                onChange={(_, v) => {
                  setDialogDirty(true);
                  const next = v as Action;
                  setDialogAction(next);
                  // Ein Test kann keine Aufzeichnung abspielen (Server: test_nicht_als_aufzeichnung).
                  if (next === 'test' && dialogBroadcastSource === 'recorded') {
                    setDialogBroadcastSource('live');
                    setDialogRecordedVideoId('');
                    setDialogRecordedError(null);
                  }
                }}
              >
                <FormControlLabel value="extra" control={<Radio />} label="Echte Sendung" />
                <FormControlLabel value="test" control={<Radio />} label="Test (nur intern)" />
              </RadioGroup>
            </FormControl>
          </Feld>

          {ersetztEchtenTermin && dialogBestand && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              An diesem Tag ist schon {AKTION_TEXT[dialogBestand.source] ?? 'ein Termin'} um{' '}
              {dialogBestand.time} Uhr eingetragen.{' '}
              <strong>
                Beim Speichern wird dieser Termin gelöscht und durch{' '}
                {dialogAction === 'test' ? 'den Test' : 'diesen Termin'} ersetzt.
              </strong>{' '}
              Zuschauer sehen ihn dann nicht mehr, Newsletter-Hinweis und Erinnerungsmail entfallen.
            </Alert>
          )}
          {ersetztTest && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Der Test an diesem Tag wird beim Speichern durch die echte Sendung ersetzt.
            </Alert>
          )}

          {dialogAction === 'test' && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="info" sx={{ mb: 1 }}>
                Ein Test ist nur intern: auf live.finanztrends.de sieht ihn nur, wer den Test-Link
                öffnet. Kein Newsletter-Hinweis, keine Erinnerungsmail, keine Bestätigung nötig.
                <Box mt={0.75}>{TEST_START_SATZ}</Box>
                <Box mt={0.75}>
                  Nach dem Test im Reiter „Heute“ auf „{BEGRIFF_LIVE.endStream}“ klicken.
                  {istHeute
                    ? ' Test gleich heute: Uhrzeit unten auf ein paar Minuten nach jetzt stellen.'
                    : ' Ein Test geht auch heute — dafür im Sendeplan auf den heutigen Tag klicken.'}
                </Box>
                <Box mt={0.75}>
                  {testLink.stand === 'da' && testLink.url ? (
                    <>
                      <Link
                        href={testLink.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                      >
                        Test-Link öffnen <OpenInNewOutlined sx={{ fontSize: 14 }} />
                      </Link>{' '}
                      — {TEST_LINK_SATZ}
                    </>
                  ) : testLink.stand === 'laedt' ? (
                    'Test-Link wird geladen …'
                  ) : (
                    `Es ist kein Test-Link eingerichtet — bitte ${TECHNIK_KONTAKT} fragen.`
                  )}
                </Box>
              </Alert>
              <Alert severity="warning">
                <strong>Achtung YouTube:</strong> Was das Studio sendet, läuft auch bei einem Test
                live auf dem öffentlichen YouTube-Kanal mit — die Weiterleitung dorthin hängt am
                Studio-Signal, nicht am Termin. Abschalten kann sie nur die Technik. Das ist
                planbar: vor dem Test {TECHNIK_KONTAKT} Bescheid geben.
              </Alert>
              <NurAdmin>
                <Typography variant="caption" color="textSecondary" display="block" mt={0.75}>
                  Technik: Mux leitet seit 30.09.2026 jeden Push per Simulcast an YouTube weiter;
                  das Ziel hängt am Stream, nicht am Termin. Vorher das Simulcast-Ziel in Mux
                  abhängen (nur im Zustand „idle“ möglich) oder ohne Push auf diesen Stream testen.
                </Typography>
              </NurAdmin>
            </Box>
          )}

          <Box display="flex" gap={2} flexWrap="wrap">
            <Feld
              titel="Uhrzeit"
              {...kz('pflicht')}
              wofuer={
                istTest
                  ? 'Wann der Test startet (Berliner Zeit). Jede Uhrzeit möglich, auf die Minute — auch nachmittags oder abends.'
                  : 'Start der Sendung (Berliner Zeit). Jede Uhrzeit möglich, 24-Stunden-Format — z. B. 10:00 oder 15:30.'
              }
              sx={{ minWidth: 150 }}
            >
              {/* Freies Zeitfeld statt Viertelstunden-Liste (Persona-Runde 1, Sandra: „Nachmittag?“). Der
                  Server nimmt jede HH:MM-Zeit an (routes/live.ts, POST /admin/schedule/override); ein
                  Bestandstermin wie 18:52 stand in der alten Liste gar nicht drin und zeigte ein leeres Feld. */}
              <TextField
                type="time"
                size="small"
                value={dialogTime.slice(0, 5)}
                onChange={(e) => {
                  setDialogDirty(true);
                  setDialogTime(e.target.value);
                }}
                inputProps={{ step: 60, 'aria-label': 'Uhrzeit' }}
                error={!/^\d{2}:\d{2}$/.test(dialogTime.slice(0, 5))}
                sx={{ minWidth: 130 }}
              />
            </Feld>

            <Feld
              titel="Was läuft zur Sendezeit?"
              {...kz('pflicht')}
              wofuer={
                dialogAction === 'test'
                  ? 'Live aus dem Studio — oder ein fertiges Video, das zur Testzeit abgespielt wird (sichtbar nur über den Test-Link).'
                  : 'Live aus dem Studio — oder ein fertiges Video, das zur Sendezeit für alle abgespielt wird.'
              }
              sx={{ flex: 1, minWidth: 240 }}
            >
              <FormControl size="small" fullWidth disabled={!dialogAdVideoPrefillOk}>
                <Select
                  value={dialogBroadcastSource}
                  onChange={(e) => {
                    const next = e.target.value as 'live' | 'recorded';
                    setDialogBroadcastSource(next);
                    if (next === 'live') setDialogRecordedVideoId('');
                    setDialogRecordedError(null);
                    // Dirty-Riegel: sonst bestätigte „Speichern & bestätigen" ohne zu speichern
                    // den ALTEN Stand, während der Dialog schon „Aufzeichnung" zeigt.
                    setDialogDirty(true);
                  }}
                >
                  <MenuItem value="live">Live aus dem Studio</MenuItem>
                  <MenuItem value="recorded" disabled={dialogAction === 'test'}>
                    Aufzeichnung abspielen
                    {dialogAction === 'test' ? ' (nicht bei einem Test)' : ''}
                  </MenuItem>
                </Select>
              </FormControl>
            </Feld>
          </Box>

          {dialogBroadcastSource === 'recorded' && (
            <Feld
              titel="Video für diese Sendung"
              {...kz('pflicht')}
              wofuer="Läuft zur geplanten Uhrzeit für alle Zuschauer gleichzeitig und ist als Aufzeichnung gekennzeichnet. Unter 5 Minuten Länge landet die Sendung nicht im Archiv und nicht auf YouTube."
            >
              <FormControl
                fullWidth
                size="small"
                error={!!dialogRecordedError}
                disabled={!dialogAdVideoPrefillOk}
              >
                <Select
                  displayEmpty
                  value={dialogRecordedVideoId}
                  onChange={(e) => {
                    setDialogRecordedVideoId(Number(e.target.value) || '');
                    setDialogRecordedError(null);
                    setDialogDirty(true);
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>
                      {dialogShowVideoLib.length === 0
                        ? 'Keine Aufzeichnungen bereit — im Reiter „Videos“ hochladen'
                        : '— bitte auswählen —'}
                    </em>
                  </MenuItem>
                  {dialogShowVideoLib.map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.title}
                      {v.duration_sec
                        ? ` (${Math.floor(v.duration_sec / 60)}:${String(v.duration_sec % 60).padStart(2, '0')} min)`
                        : ''}
                    </MenuItem>
                  ))}
                </Select>
                {dialogRecordedError && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {dialogRecordedError}
                  </Typography>
                )}
              </FormControl>
            </Feld>
          )}

          <Feld
            titel="Moderation"
            {...kz('pflicht')}
            wofuer={
              istTest
                ? `Wer den Test durchführt. Auch beim Test nötig, damit er sich speichern lässt — Mails gehen dabei an niemanden. Fehlt jemand in der Liste: ${TECHNIK_KONTAKT} fragen.`
                : `Wer moderiert? Der Name erscheint vor der Sendung auf live.finanztrends.de und im Newsletter-Hinweis. Ist die Sendung am Sendetag um 08:30 Uhr noch nicht bestätigt, bekommt diese Person eine Mail mit Bestätigungs-Link. Fehlt jemand in der Liste: ${TECHNIK_KONTAKT} fragen.`
            }
          >
            <FormControl
              fullWidth
              size="small"
              error={!!dialogHostError}
              disabled={!availableHostsLoaded && availableHosts.length === 0}
            >
              <Select
                displayEmpty
                value={dialogHostEmail}
                onChange={(e) => {
                  setDialogDirty(true);
                  setDialogHostEmail(String(e.target.value));
                  if (dialogHostError) setDialogHostError(null);
                }}
              >
                <MenuItem value="">
                  <em>— bitte auswählen —</em>
                </MenuItem>
                {/* Currently-selected host that is no longer in the active list
                    (e.g. user got removed since the show was originally scheduled).
                    Render so the row isn't accidentally cleared on edit. */}
                {dialogHostEmail && !availableHosts.some((h) => h.email === dialogHostEmail) && (
                  <MenuItem value={dialogHostEmail}>
                    {dialogHostEmail} (nicht mehr freigeschaltet)
                  </MenuItem>
                )}
                {availableHosts.map((h) => (
                  <MenuItem key={h.email} value={h.email}>
                    {hostName(h)}
                    {doppelteNamen.has(hostName(h)) ? ` (${h.email})` : ''}
                  </MenuItem>
                ))}
              </Select>
              {dialogHostError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {dialogHostError}
                </Typography>
              )}
            </FormControl>
          </Feld>

          <Feld
            titel={`Themen (${dialogTopicItems.filter((t) => t.trim()).length} von höchstens 5)`}
            {...kz('pflicht')}
            wofuer={
              istTest
                ? 'Auch beim Test nötig, damit er sich speichern lässt; sehen kann sie nur, wer den Test-Link öffnet. Mindestens ein Thema, je bis zu 120 Zeichen.'
                : 'Stehen vor der Sendung auf live.finanztrends.de unter dem Countdown („Heute im Stream“) und geben der Aufzeichnung im Archiv ihren Titel, wenn kein eigener gesetzt ist. Mindestens ein Thema, je bis zu 120 Zeichen — auch bei einer Aufzeichnung.'
            }
            aktion={
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setDialogDirty(true);
                  setDialogTopicItems((prev) => (prev.length >= 5 ? prev : [...prev, '']));
                  if (dialogTopicsError) setDialogTopicsError(null);
                }}
                disabled={dialogTopicItems.length >= 5 || dialogTopicsStand.stand !== 'ok'}
              >
                + Thema
              </Button>
            }
          >
            {themenFehlen && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                <strong>Für diesen Termin sind keine Themen gespeichert</strong>, obwohl er{' '}
                {dialogExistingStatus === 'confirmed' ? 'schon bestätigt' : 'angelegt'} ist.{' '}
                {THEMEN_FEHLEN_FOLGE} Bitte Themen eintragen und speichern.
              </Alert>
            )}
            {/* Themen hängen am Tag, nicht am Termin (live_show_topics, Schlüssel show_date): ein neuer
                Termin erbt die eines früheren oder gelöschten (Persona-Runde 3, Sandra/Felix). */}
            {dialogExistingSource === null &&
              dialogTopicsStand.stand === 'ok' &&
              dialogTopicsStand.gespeichert > 0 && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Für diesen Tag sind schon Themen gespeichert (von einem früheren oder gelöschten
                  Termin). Bitte prüfen und bei Bedarf ändern.
                </Alert>
              )}
            {dialogTopicsStand.stand === 'fehler' && (
              <Alert severity="error" sx={{ mb: 1 }}>
                Die gespeicherten Themen konnten nicht geladen werden. Speichern ist deshalb
                gesperrt, damit sie nicht überschrieben werden — bitte das Fenster schließen und den
                Tag neu öffnen.
              </Alert>
            )}
            {dialogTopicItems.map((topic, idx) => (
              <Box key={`topic-${idx}`} display="flex" alignItems="center" gap={1} mb={1}>
                <TextField
                  value={topic}
                  onChange={(e) => {
                    const v = e.target.value.slice(0, 120);
                    setDialogDirty(true);
                    setDialogTopicItems((prev) => prev.map((t, i) => (i === idx ? v : t)));
                    if (dialogTopicsError) setDialogTopicsError(null);
                  }}
                  placeholder={
                    idx === 0 ? 'z. B. Bayer Q1-Zahlen — Rallye fortsetzen?' : `Thema ${idx + 1}`
                  }
                  size="small"
                  fullWidth
                  inputProps={{ maxLength: 120 }}
                  disabled={dialogTopicsStand.stand !== 'ok'}
                  error={!!dialogTopicsError && idx === 0}
                />
                <Tooltip title="Thema entfernen">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDialogDirty(true);
                        setDialogTopicItems((prev) =>
                          prev.length <= 1 ? [''] : prev.filter((_, i) => i !== idx),
                        );
                      }}
                      disabled={dialogTopicItems.length === 1 && !topic}
                      aria-label={`Thema ${idx + 1} entfernen`}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ))}
            {dialogTopicsError && (
              <Typography variant="caption" color="error" display="block" sx={{ ml: 0.5 }}>
                {dialogTopicsError}
              </Typography>
            )}
          </Feld>

          {/* B7: Newsletter-Schlagzeile des Sendetags — nur echte Sendungen erscheinen im Newsletter. */}
          {dialogAction === 'extra' && dialogDate && (
            <AdHeadline
              datum={dialogDate}
              wert={dialogHeadline}
              maxLength={dialogHeadlineMax}
              geladen={dialogHeadlinePrefillOk}
              onChange={(v) => {
                setDialogDirty(true);
                setDialogHeadline(v);
              }}
              zeit={dialogTime}
              moderatorName={vorschauModeration}
              istAufzeichnung={dialogBroadcastSource === 'recorded'}
              speichernKnoepfe={speichernKnoepfe}
            />
          )}

          {/* Werbevideo: bei einem Test ausgeblendet (Persona-Runde 1). Der Wert bleibt im Formular und
              wird unverändert mitgeschickt (Prefill-Riegel unten in `speichern`), es geht also nichts verloren. */}
          {!istTest && (
            <Feld
              titel={BEGRIFF_LIVE.autoRoll}
              {...kz('optional')}
              wofuer={
                <>
                  Läuft auf live.finanztrends.de, nachdem die Sendung mit „{BEGRIFF_LIVE.endStream}“
                  beendet wurde. Ohne Auswahl läuft der Standard aus dem Reiter „Videos“.
                  {dialogAutoRoll.rerunAn &&
                    ' An Werbetagen vor einem neuen Webinar läuft kein Video — die Seite wirbt dann für das Webinar.'}
                </>
              }
            >
              <FormControl fullWidth size="small" disabled={!dialogAdVideoPrefillOk}>
                <Select
                  value={
                    typeof dialogAdVideoMode === 'number'
                      ? String(dialogAdVideoMode)
                      : dialogAdVideoMode
                  }
                  onChange={(e) => {
                    const v = e.target.value;
                    setDialogDirty(true);
                    if (v === 'default' || v === 'no-roll') setDialogAdVideoMode(v);
                    else setDialogAdVideoMode(parseInt(v, 10));
                  }}
                >
                  <MenuItem value="default">{werbevideoStandardText}</MenuItem>
                  <MenuItem value="no-roll">Kein Werbevideo nach dieser Sendung</MenuItem>
                  <Divider />
                  {/* Persona-Runde 3 (Felix): „Standard (X)" oben und „X · Standard" hier sahen gleich aus. */}
                  <ListSubheader sx={{ lineHeight: 2.2 }}>
                    Fest gewählt — bleibt, auch wenn der Standard wechselt
                  </ListSubheader>
                  {dialogAdVideoLib.length === 0 && (
                    <MenuItem value="" disabled>
                      Keine Werbevideos bereit — im Reiter „Videos“ hochladen
                    </MenuItem>
                  )}
                  {dialogAdVideoLib.map((v) => (
                    <MenuItem key={v.id} value={String(v.id)}>
                      {v.title}
                      {v.is_default ? ' · ist zurzeit der Standard' : ''}
                      {knopfText(v)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {videoOhneAngebot && (
                <Alert
                  severity="warning"
                  sx={{ mt: 1 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() =>
                        setAngebotFrage({
                          video: { id: videoOhneAngebot.id, title: videoOhneAngebot.title },
                        })
                      }
                    >
                      Angebot festlegen
                    </Button>
                  }
                >
                  „{videoOhneAngebot.title}“ zeigt nach der Sendung keinen Kaufknopf.
                </Alert>
              )}
            </Feld>
          )}

          {/* „Optional“ steht schon als Kennzeichen am Kopf (MehrOptionen) — der Satz sagt deshalb nur,
              was bei leeren Feldern passiert (Persona-Runde 1: „Optional“ stand doppelt). */}
          <MehrOptionen
            titel="Titel und interne Notiz"
            erklaerung="Kann leer bleiben — ohne Titel heißt die Aufzeichnung nach den Themen."
          >
            <Feld
              titel="Titel der Aufzeichnung"
              {...kz('optional')}
              wofuer="Name der Sendung im Archiv auf live.finanztrends.de. Leer = die Themen werden als Titel benutzt."
            >
              <TextField
                size="small"
                value={dialogLabel}
                onChange={(e) => {
                  setDialogDirty(true);
                  setDialogLabel(e.target.value.slice(0, 120));
                }}
                fullWidth
              />
            </Feld>
            <Feld
              titel="Interne Notiz"
              {...kz('optional')}
              wofuer="Nur hier im Dashboard sichtbar, nie für Zuschauer."
              sx={{ mb: 0 }}
            >
              <TextField
                size="small"
                value={dialogNotes}
                onChange={(e) => {
                  setDialogDirty(true);
                  setDialogNotes(e.target.value);
                }}
                multiline
                rows={2}
                fullWidth
              />
            </Feld>
          </MehrOptionen>

          {/* A1: Hauptaktion mit Wirkung in einem Satz daneben. */}
          <Paper
            variant="outlined"
            sx={{ p: 2, mt: 2, bgcolor: '#ffffff', borderColor: '#e5e7eb' }}
          >
            {dialogAction === 'test' ? (
              <Typography variant="body2" color="textSecondary">
                Ein Test braucht keine Bestätigung — „Test speichern“ genügt.
              </Typography>
            ) : zeigeWiederBestaetigen ? (
              <Typography variant="body2" color="textSecondary">
                Die Sendung ist abgesagt. „Speichern“ ändert nur die Angaben, sie bleibt abgesagt.
                „Speichern &amp; wieder ansetzen“ bestätigt sie erneut: sie erscheint wieder auf
                live.finanztrends.de, und die Erinnerungsmail geht 15 Minuten vor Beginn raus. Ist
                der Newsletter des Sendetags schon verschickt ({NEWSLETTER_VERSAND} Uhr), enthält er
                keinen Hinweis mehr.
              </Typography>
            ) : zeigeBestaetigenKnopf ? (
              <Typography variant="body2" color="textSecondary">
                <strong>Bestätigen</strong> heißt: die Sendung erscheint auf live.finanztrends.de,
                der Newsletter des Sendetags ({NEWSLETTER_VERSAND} Uhr) weist darauf hin, und 15
                Minuten vor Beginn geht eine Erinnerungsmail an alle, die sich dafür eingetragen
                haben.{' '}
                {fristHeuteVorbei ? (
                  <strong>
                    Heute ist es schon nach {BESTAETIGEN_BIS} Uhr: der Newsletter ist verschickt,
                    und ohne Bestätigung wird die Sendung auf live.finanztrends.de nicht angekündigt
                    {dialogBroadcastSource === 'recorded'
                      ? ' und eine Aufzeichnung startet nicht.'
                      : ' (kein Countdown, keine Erinnerungsmail).'}
                  </strong>
                ) : (
                  <>
                    <strong>
                      Ohne Bestätigung wird die Sendung am Sendetag um {BESTAETIGEN_BIS} Uhr
                      automatisch abgesagt
                    </strong>
                    {dialogBroadcastSource === 'recorded'
                      ? ' — und eine nicht bestätigte Aufzeichnung startet nicht.'
                      : '. Sendet das Studio zwischen 07:00 und 08:50 Uhr schon ein Signal, gilt eine Live-Sendung automatisch als bestätigt.'}
                  </>
                )}{' '}
                „Speichern &amp; bestätigen“ prüft vorher die Pflichtfelder — ohne Moderation und
                mindestens ein Thema wird nichts bestätigt.
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: '#1e6b52' }}>
                Die Sendung ist bestätigt. Speichern ändert daran nichts.
              </Typography>
            )}
          </Paper>

          {/* B8: Export-Sperre — sofort wirksam, eigener Endpunkt, getrennt vom Formular. Bei einem Test
              ausgeblendet (Persona-Runde 1: weckte falsche Hoffnung — sie hält den Live-Simulcast nicht auf).
              Was sie wirklich tut (live-streaming-system.md § Was YouTube-seitig eingestellt ist): ai-prod
              zieht den Tag nach der Sendung nicht — kein geschnittenes YouTube-Video, kein Artikel. Der
              Mux-Simulcast läuft daran vorbei; dessen Mitschnitt bleibt auf YouTube „nicht gelistet“.
              Einblendungen liegen als HTML über dem Player (public/live/index.html, uebernimmEinblendungen)
              und sind weder im Videobild noch in Aufzeichnung oder Simulcast. */}
          {!istTest && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mt: 2,
                bgcolor: '#fcfcfd',
                borderColor: '#d1d5db',
                borderStyle: 'dashed',
              }}
            >
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Kein YouTube-Video und kein Artikel nach der Sendung
                </Typography>
                <Kennzeichen art="optional" erklaerung={KENNZEICHEN_LIVE.optional} />
                <Chip size="small" variant="outlined" label="wirkt sofort — ohne „Speichern“" />
              </Box>
              <Typography variant="caption" color="textSecondary" component="div" sx={{ mb: 0.75 }}>
                <strong>Nur für die Ausnahme:</strong> wenn im <em>gesprochenen</em> Teil etwas
                Befristetes steckt, das nicht dauerhaft im Netz stehen darf (z. B. ein Angebot, das
                der Moderator nur für heute nennt). Einblendungen sind kein Grund: sie liegen auf
                live.finanztrends.de über dem Video und sind weder in der Aufzeichnung noch auf
                YouTube zu sehen.
              </Typography>
              <Typography variant="caption" color="textSecondary" component="div" sx={{ mb: 0.75 }}>
                Gesperrt heißt: nach der Sendung entsteht kein YouTube-Video und kein automatischer
                Finanztrends-Artikel; im Archiv auf live.finanztrends.de bleibt die Aufzeichnung
                abrufbar. Die Live-Übertragung selbst läuft trotzdem auf YouTube mit, und ihr
                Mitschnitt bleibt dort als „nicht gelistet“ unter seinem Link erreichbar. Soll gar
                nichts auf YouTube laufen, ist das planbar: vorher {TECHNIK_KONTAKT} Bescheid geben.
              </Typography>
              <Typography variant="caption" color="textSecondary" component="div" sx={{ mb: 1 }}>
                Warum sofort und ohne „Speichern“, anders als alle anderen Felder hier: die Sperre
                soll nicht an einem vergessenen Klick hängen. Vor der Sendung setzen.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={dialogExportBlocked}
                    disabled={exportBlockBusy}
                    onChange={(e) => {
                      if (e.target.checked) setExportSperreOffen(true);
                      else schreibeExportSperre(false);
                    }}
                  />
                }
                label={
                  dialogExportBlocked
                    ? 'Gesperrt: kein YouTube-Video danach, kein Artikel'
                    : 'Sperren: kein YouTube-Video danach, kein Artikel'
                }
              />
            </Paper>
          )}

          {/* A3: Absagen und Löschen — Wirkung VOR dem Klick, deutlich abgesetzt. */}
          {dialogExistingSource !== null && (
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e5e7eb' }}>
              {zeigeAbsagen && (
                <Box mb={2}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      setAbsagenFehler(null);
                      setAbsagenOffen(true);
                    }}
                    disabled={arbeitet}
                  >
                    Sendung absagen
                  </Button>
                  <Typography variant="caption" color="textSecondary" component="div" mt={0.5}>
                    Der Termin bleibt im Sendeplan (rot) und lässt sich wieder ansetzen. Zuschauer
                    sehen ihn nicht mehr, der Newsletter weist nicht darauf hin, keine
                    Erinnerungsmail. {absageMailSatz}
                  </Typography>
                </Box>
              )}
              <Box>
                <Button
                  variant="text"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => {
                    setLoeschenFehler(null);
                    setLoeschenOffen(true);
                  }}
                  disabled={arbeitet}
                >
                  Termin löschen
                </Button>
                <Typography variant="caption" color="textSecondary" component="div" mt={0.5}>
                  Der Termin verschwindet ganz aus dem Sendeplan, mit Themen und
                  Newsletter-Schlagzeile. Zum Ausfallenlassen einer Sendung lieber „Sendung
                  absagen“.
                </Typography>
              </Box>
            </Box>
          )}

          {dialogHistory.length > 0 && (
            <Box mt={3}>
              <NurAdmin bereich titel="Änderungsverlauf">
                {dialogHistory.map((h, i) => (
                  <Typography
                    key={`${h.changed_at}-${h.field}-${i}`}
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    {moment(h.changed_at).format('DD.MM. HH:mm')} ·{' '}
                    {h.field === '*'
                      ? h.new_value === 'created'
                        ? 'Termin angelegt'
                        : 'Termin gelöscht'
                      : `${OVERRIDE_FIELD_LABELS[h.field] ?? h.field}: ${h.old_value ?? '—'} → ${
                          h.new_value ?? '—'
                        }`}{' '}
                    · {h.changed_by_email ?? 'unbekannt'}
                  </Typography>
                ))}
              </NurAdmin>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={
            istHandy
              ? {
                  justifyContent: 'flex-end',
                  px: 2,
                  py: 1,
                  borderTop: '1px solid #e5e7eb',
                  '& .MuiButton-root': { whiteSpace: 'nowrap' },
                }
              : { justifyContent: 'space-between', px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }
          }
        >
          {/* Immer „Schließen“ (Persona-Runde 1: der Knopf sprang zwischen „Schließen“ und „Abbrechen“).
              Ungespeicherte Änderungen fragt `dialogSchliessen` ohnehin nach. Auf dem Handy sitzt es als ✕
              im Kopf, damit der Fuß in eine Zeile passt. */}
          {!istHandy && (
            <Button onClick={dialogSchliessen} disabled={arbeitet}>
              Schließen
            </Button>
          )}
          <Box
            display="flex"
            gap={1}
            flexWrap={istHandy ? 'nowrap' : 'wrap'}
            justifyContent="flex-end"
          >
            {dialogAction === 'test' ? (
              <Button variant="contained" onClick={() => handleNurSpeichern()} disabled={arbeitet}>
                {dialogBusy ? 'Speichert…' : 'Test speichern'}
              </Button>
            ) : zeigeBestaetigenKnopf ? (
              <>
                <Button onClick={() => handleNurSpeichern()} disabled={arbeitet}>
                  Nur speichern
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => handleSpeichernUndBestaetigen()}
                  disabled={arbeitet}
                >
                  {dialogBusy
                    ? 'Speichert…'
                    : dialogConfirming
                      ? 'Bestätigt…'
                      : 'Speichern & bestätigen'}
                </Button>
              </>
            ) : zeigeWiederBestaetigen ? (
              <>
                <Button onClick={() => handleSpeichernUndBestaetigen()} disabled={arbeitet}>
                  Speichern &amp; wieder ansetzen
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleNurSpeichern()}
                  disabled={arbeitet}
                >
                  {dialogBusy ? 'Speichert…' : 'Speichern'}
                </Button>
              </>
            ) : (
              <Button variant="contained" onClick={() => handleNurSpeichern()} disabled={arbeitet}>
                {dialogBusy ? 'Speichert…' : 'Speichern'}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <GefahrBestaetigung
        open={absagenOffen}
        titel={`Sendung am ${datumKurz} absagen?`}
        wirkung={
          <>
            Die Sendung verschwindet für Zuschauer von live.finanztrends.de (dort steht dann die
            nächste bestätigte Sendung). Der Newsletter des Sendetags weist nicht darauf hin, es
            geht keine Erinnerungsmail raus, und eine geplante Aufzeichnung startet nicht. Ist der
            Newsletter schon verschickt ({NEWSLETTER_VERSAND} Uhr), lässt sich sein Hinweis nicht
            mehr zurückholen. {absageMailSatz} Der Termin bleibt im Sendeplan und lässt sich wieder
            ansetzen.
            {dialogDirty &&
              ' Nicht gespeicherte Änderungen in diesem Fenster gehen dabei verloren.'}
          </>
        }
        knopfText="Sendung absagen"
        laeuft={dialogCancelling}
        fehler={absagenFehler}
        onBestaetigen={handleCancelShow}
        onAbbrechen={() => setAbsagenOffen(false)}
      />

      <GefahrBestaetigung
        open={loeschenOffen}
        titel={`Termin am ${datumKurz} löschen?`}
        wirkung={
          <>
            Der Termin verschwindet ganz aus dem Sendeplan — mit Themen und Newsletter-Schlagzeile.
            Zuschauer bekommen keinen Hinweis; auf live.finanztrends.de steht dann einfach die
            nächste Sendung. Das lässt sich nicht rückgängig machen. Soll die Sendung nur ausfallen,
            besser „Sendung absagen“.
          </>
        }
        knopfText="Endgültig löschen"
        bestaetigungsWort={datumKurz}
        laeuft={dialogDeleting}
        fehler={loeschenFehler}
        onBestaetigen={handleDelete}
        onAbbrechen={() => setLoeschenOffen(false)}
      />

      <GefahrBestaetigung
        open={exportSperreOffen}
        titel={`YouTube-Video und Artikel zur Sendung vom ${datumKurz} sperren?`}
        wirkung={
          <>
            Nach der Sendung entsteht kein YouTube-Video und kein automatischer
            Finanztrends-Artikel. Auf live.finanztrends.de bleibt die Aufzeichnung im Archiv
            abrufbar. Die Live-Übertragung auf YouTube läuft trotzdem mit, ihr Mitschnitt bleibt
            dort „nicht gelistet“ unter seinem Link erreichbar. Das wirkt sofort, ohne „Speichern“.
            Ist schon ein Video auf YouTube, muss es dort von Hand entfernt werden — dafür{' '}
            {TECHNIK_KONTAKT} fragen.
          </>
        }
        knopfText="Sperren"
        laeuft={exportBlockBusy}
        onBestaetigen={() => schreibeExportSperre(true)}
        onAbbrechen={() => setExportSperreOffen(false)}
      />

      <GefahrBestaetigung
        open={ersetzenNach !== null}
        titel="Bestehenden Termin ersetzen?"
        wirkung={
          dialogBestand ? (
            <>
              An diesem Tag ist schon {AKTION_TEXT[dialogBestand.source] ?? 'ein Termin'} um{' '}
              {dialogBestand.time} Uhr eingetragen. Beim Speichern wird er gelöscht und durch{' '}
              {dialogAction === 'test' ? 'den Test' : 'diesen Termin'} ersetzt. Zuschauer sehen die
              bisherige Sendung dann nicht mehr; Newsletter-Hinweis und Erinnerungsmail entfallen.
            </>
          ) : (
            ''
          )
        }
        knopfText="Ersetzen und speichern"
        laeuft={dialogBusy}
        onBestaetigen={() => {
          const nach = ersetzenNach;
          setErsetzenNach(null);
          if (nach === 'bestaetigen') handleSpeichernUndBestaetigen({ ersetzenGesehen: true });
          else handleNurSpeichern({ ersetzenGesehen: true });
        }}
        onAbbrechen={() => setErsetzenNach(null)}
      />

      <AngebotDialog
        video={angebotFrage?.video ?? null}
        meldung={angebotFrage?.meldung}
        onFertig={(ergebnis) => {
          const frage = angebotFrage;
          setAngebotFrage(null);
          if (frage && ergebnis !== 'abgebrochen') {
            const lage: AngebotLage = ergebnis === 'angebot' ? 'ok' : 'bewusst_ohne';
            setDialogAdVideoLib((liste) =>
              liste.map((v) => (v.id === frage.video.id ? { ...v, angebot_lage: lage } : v)),
            );
          }
          frage?.aufloesen?.(ergebnis);
        }}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            variant="filled"
            onClose={() => setToast(null)}
            sx={{ minWidth: 280 }}
          >
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default Schedule;
